import React, { useState, useEffect, useCallback, useRef } from 'react';
import Hamburger from '../components/Hamburger';
import { useAuth } from '../context/AuthContext';
import useRealTimeUpdates from '../hooks/useRealTimeUpdates';
import './ListPage.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function ListPage() {
    const { user } = useAuth();
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);

    // Add-task modal (replaces window.prompt so we can control mobile keyboard + remove browser dialog header)
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [newTaskText, setNewTaskText] = useState('');
    const [newTaskNote, setNewTaskNote] = useState('');
    const addTaskInputRef = useRef(null);

    const openAddTaskModal = useCallback(() => {
        setNewTaskText('');
        setNewTaskNote('');
        setShowAddTaskModal(true);
    }, []);

    const closeAddTaskModal = useCallback(() => {
        setShowAddTaskModal(false);
        setNewTaskText('');
        setNewTaskNote('');
    }, []);

    // Focus input when modal opens (helps on mobile)
    useEffect(() => {
        if (!showAddTaskModal) return;
        const t = setTimeout(() => addTaskInputRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [showAddTaskModal]);

    // Close modal on Escape
    useEffect(() => {
        if (!showAddTaskModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeAddTaskModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showAddTaskModal, closeAddTaskModal]);

    // Edit-task modal (replaces window.prompt for editing task names)
    const [showEditTaskModal, setShowEditTaskModal] = useState(false);
    const [editTaskText, setEditTaskText] = useState('');
    const [editTaskDueDate, setEditTaskDueDate] = useState(null);
    const [editingTask, setEditingTask] = useState(null);
    const [editingListId, setEditingListId] = useState(null);
    const editTaskInputRef = useRef(null);

    const openEditTaskModal = useCallback((listId, task) => {
        setEditingListId(listId);
        setEditingTask(task);
        setEditTaskText(task?.text ?? '');
        // Convert due_date string to Date for future picker
        if (task?.due_date) {
            setEditTaskDueDate(new Date(task.due_date));
        } else {
            setEditTaskDueDate(null);
        }
        setShowEditTaskModal(true);
    }, []);

    const closeEditTaskModal = useCallback(() => {
        setShowEditTaskModal(false);
        setEditTaskText('');
        setEditTaskDueDate(null);
        setEditingTask(null);
        setEditingListId(null);
    }, []);

    // Focus input when edit modal opens
    useEffect(() => {
        if (!showEditTaskModal) return;
        const t = setTimeout(() => editTaskInputRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [showEditTaskModal]);

    // Close edit modal on Escape
    useEffect(() => {
        if (!showEditTaskModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeEditTaskModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showEditTaskModal, closeEditTaskModal]);

    // Add-section modal (replaces window.prompt for adding sections)
    const [showAddSectionModal, setShowAddSectionModal] = useState(false);
    const [newSectionTitle, setNewSectionTitle] = useState('');
    const [addSectionListId, setAddSectionListId] = useState(null);
    const addSectionInputRef = useRef(null);

    const openAddSectionModal = useCallback((listId) => {
        setAddSectionListId(listId);
        setNewSectionTitle('');
        setShowAddSectionModal(true);
    }, []);

    const closeAddSectionModal = useCallback(() => {
        setShowAddSectionModal(false);
        setNewSectionTitle('');
        setAddSectionListId(null);
    }, []);

    // Focus input when add section modal opens
    useEffect(() => {
        if (!showAddSectionModal) return;
        const t = setTimeout(() => addSectionInputRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [showAddSectionModal]);

    // Close add section modal on Escape
    useEffect(() => {
        if (!showAddSectionModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeAddSectionModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showAddSectionModal, closeAddSectionModal]);

    // Rename-section modal (replaces window.prompt for renaming sections)
    const [showRenameSectionModal, setShowRenameSectionModal] = useState(false);
    const [renameSectionTitle, setRenameSectionTitle] = useState('');
    const [renamingSection, setRenamingSection] = useState(null);
    const renameSectionInputRef = useRef(null);

    const openRenameSectionModal = useCallback((section) => {
        setRenamingSection(section);
        setRenameSectionTitle(section?.title ?? '');
        setShowRenameSectionModal(true);
    }, []);

    const closeRenameSectionModal = useCallback(() => {
        setShowRenameSectionModal(false);
        setRenameSectionTitle('');
        setRenamingSection(null);
    }, []);

    // Focus input when rename section modal opens
    useEffect(() => {
        if (!showRenameSectionModal) return;
        const t = setTimeout(() => renameSectionInputRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [showRenameSectionModal]);

    // Close rename section modal on Escape
    useEffect(() => {
        if (!showRenameSectionModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeRenameSectionModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showRenameSectionModal, closeRenameSectionModal]);

    // Edit-note modal (for adding/editing task notes)
    const [showEditNoteModal, setShowEditNoteModal] = useState(false);
    const [editNoteText, setEditNoteText] = useState('');
    const [editingNoteTask, setEditingNoteTask] = useState(null);
    const [editingNoteListId, setEditingNoteListId] = useState(null);
    const editNoteInputRef = useRef(null);

    const openEditNoteModal = useCallback((listId, task) => {
        setEditingNoteListId(listId);
        setEditingNoteTask(task);
        setEditNoteText(task?.note ?? '');
        setShowEditNoteModal(true);
    }, []);

    const closeEditNoteModal = useCallback(() => {
        setShowEditNoteModal(false);
        setEditNoteText('');
        setEditingNoteTask(null);
        setEditingNoteListId(null);
    }, []);

    // Focus input when edit note modal opens
    useEffect(() => {
        if (!showEditNoteModal) return;
        const t = setTimeout(() => editNoteInputRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [showEditNoteModal]);

    // Close edit note modal on Escape
    useEffect(() => {
        if (!showEditNoteModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeEditNoteModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showEditNoteModal, closeEditNoteModal]);

    // Add user to list modal
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [addUserUsername, setAddUserUsername] = useState('');
    const [addUserListId, setAddUserListId] = useState(null);
    const addUserUsernameRef = useRef(null);

    const openAddUserModal = useCallback((listId) => {
        setAddUserListId(listId);
        setAddUserUsername('');
        setShowAddUserModal(true);
    }, []);

    const closeAddUserModal = useCallback(() => {
        setShowAddUserModal(false);
        setAddUserUsername('');
        setAddUserListId(null);
    }, []);

    // Focus input when add user modal opens
    useEffect(() => {
        if (!showAddUserModal) return;
        const t = setTimeout(() => addUserUsernameRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [showAddUserModal]);

    // Close add user modal on Escape
    useEffect(() => {
        if (!showAddUserModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeAddUserModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showAddUserModal, closeAddUserModal]);

    // Manage users modal (view and remove users from list)
    const [showManageUsersModal, setShowManageUsersModal] = useState(false);
    const [manageUsersListId, setManageUsersListId] = useState(null);
    const [listUsers, setListUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const openManageUsersModal = useCallback(async (listId) => {
        setManageUsersListId(listId);
        setShowManageUsersModal(true);
        setLoadingUsers(true);
        
        try {
            const response = await fetch(`${API_URL}/api/lists/${listId}/access`);
            if (response.ok) {
                const users = await response.json();
                setListUsers(users);
            } else {
                console.error('Failed to fetch list users');
                setListUsers([]);
            }
        } catch (error) {
            console.error('Error fetching list users:', error);
            setListUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    const closeManageUsersModal = useCallback(() => {
        setShowManageUsersModal(false);
        setManageUsersListId(null);
        setListUsers([]);
    }, []);

    // Close manage users modal on Escape
    useEffect(() => {
        if (!showManageUsersModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeManageUsersModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showManageUsersModal, closeManageUsersModal]);

    // Bulk select + move
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
    const [bulkMoveInProgress, setBulkMoveInProgress] = useState(false);
    
    // Bulk add tasks
    const [showBulkAddModal, setShowBulkAddModal] = useState(false);
    const [bulkAddTaskText, setBulkAddTaskText] = useState('');
    const [bulkAddTaskNote, setBulkAddTaskNote] = useState('');
    const bulkAddInputRef = useRef(null);

    // React-controlled bulk move dropdown (avoid relying on Bootstrap JS)
    const [bulkMoveMenu, setBulkMoveMenu] = useState({
        open: false,
        x: 0,
        y: 0,
        listId: null,
    });
    const bulkMoveMenuRef = useRef(null);

    // React-controlled bulk delete dropdown
    const [bulkDeleteMenu, setBulkDeleteMenu] = useState({
        open: false,
        x: 0,
        y: 0,
        listId: null,
    });
    const bulkDeleteMenuRef = useRef(null);

    const clearTaskSelection = useCallback(() => {
        setSelectedTaskIds(new Set());
    }, []);

    const toggleTaskSelected = useCallback((taskId) => {
        setSelectedTaskIds((prev) => {
            const next = new Set(prev);
            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            return next;
        });
    }, []);

    const toggleSelectAllInSection = useCallback((taskIds) => {
        if (!Array.isArray(taskIds)) return;
        setSelectedTaskIds((prev) => {
            const next = new Set(prev);
            const allSelected = taskIds.length > 0 && taskIds.every((id) => next.has(id));
            if (allSelected) {
                taskIds.forEach((id) => next.delete(id));
                return next;
            }
            taskIds.forEach((id) => next.add(id));
            return next;
        });
    }, []);

    const handleBulkMoveSelectedTasks = useCallback(async ({ listId, taskIds, targetActiveSectionId }) => {
        if (!Number.isInteger(listId)) return;
        if (!Array.isArray(taskIds) || taskIds.length === 0) return;
        if (!Number.isInteger(targetActiveSectionId)) return;

        try {
            setBulkMoveInProgress(true);

            const response = await fetch(`${API_URL}/api/tasks/bulk-move`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    listId,
                    taskIds,
                    targetActiveSectionId,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to move tasks');
            }

            // Server will broadcast BULK_TASKS_MOVED; we clear selection locally.
            clearTaskSelection();
            setIsMultiSelectMode(false);
            setBulkMoveMenu((prev) => ({ ...prev, open: false }));
        } catch (err) {
            console.error('Error bulk moving tasks:', err);
            alert(err.message || 'Error moving tasks. Please try again.');
        } finally {
            setBulkMoveInProgress(false);
        }
    }, [clearTaskSelection]);

    const handleBulkDeleteSelectedTasks = useCallback(async ({ listId, taskIds }) => {
        if (!Number.isInteger(listId)) return;
        if (!Array.isArray(taskIds) || taskIds.length === 0) return;

        try {
            const response = await fetch(`${API_URL}/api/tasks/bulk`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    listId,
                    taskIds,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to delete tasks');
            }

            // Server will broadcast BULK_TASKS_DELETED; we clear selection locally.
            clearTaskSelection();
            setIsDeleteMode(false);
            setBulkDeleteMenu((prev) => ({ ...prev, open: false }));
        } catch (err) {
            console.error('Error bulk deleting tasks:', err);
            alert(err.message || 'Error deleting tasks. Please try again.');
        }
    }, [clearTaskSelection]);

    // Bulk delete removed
    const [showCreateListModal, setShowCreateListModal] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingList, setEditingList] = useState(null);
    const [newListName, setNewListName] = useState('');
    const [newListDescription, setNewListDescription] = useState('');
    const [newListTaskOrder, setNewListTaskOrder] = useState('alphabetical_asc');
    const [newListType, setNewListType] = useState('checklist'); // New state for list type
    const createListInputRef = useRef(null);

    const openCreateListModal = useCallback(() => {
        setNewListName('');
        setNewListDescription('');
        setNewListTaskOrder('alphabetical_asc');
        setNewListType('checklist');
        setEditingActiveHeader('');
        setEditingCompletedHeader('');
        setShowCreateListModal(true);
    }, []);

    const closeCreateListModal = useCallback(() => {
        setShowCreateListModal(false);
        setNewListName('');
        setNewListDescription('');
        setNewListTaskOrder('alphabetical_asc');
        setNewListType('checklist');
        setEditingActiveHeader('');
        setEditingCompletedHeader('');
    }, []);

    // Focus input when modal opens
    useEffect(() => {
        if (!showCreateListModal) return;
        const t = setTimeout(() => createListInputRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [showCreateListModal]);

    // Close modal on Escape
    useEffect(() => {
        if (!showCreateListModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeCreateListModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showCreateListModal, closeCreateListModal]);
    const [draggedTaskId, setDraggedTaskId] = useState(null);
    const [isReorderingTasks, setIsReorderingTasks] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [touchStartTime, setTouchStartTime] = useState(null);
    const [editingActiveHeader, setEditingActiveHeader] = useState('');
    const [editingCompletedHeader, setEditingCompletedHeader] = useState('');
    const [editingTaskOrderType, setEditingTaskOrderType] = useState('custom');
    const [editingListType, setEditingListType] = useState('checklist'); // State for editing list type
    // Initialize selectedListId from localStorage if available
    const [selectedListId, setSelectedListId] = useState(() => {
        try {
            const saved = localStorage.getItem('lastSelectedListId');
            return saved ? parseInt(saved, 10) : null;
        } catch (error) {
            console.error('Error reading lastSelectedListId from localStorage:', error);
            return null;
        }
    });
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
    const actionsMenuRef = useRef(null);
    
    // Load collapsed sections state from localStorage
    const loadCollapsedSections = useCallback((listId) => {
        try {
            const key = `collapsedSections_${listId}`;
            const saved = localStorage.getItem(key);
            if (saved) {
                const data = JSON.parse(saved);
                return {
                    completed: data.completed || false,
                    activeSectionIds: new Set(data.activeSectionIds || [])
                };
            }
        } catch (error) {
            console.error('Error loading collapsed sections from localStorage:', error);
        }
        return { completed: false, activeSectionIds: new Set() };
    }, []);

    // Save collapsed sections state to localStorage
    const saveCollapsedSections = useCallback((listId, completed, activeSectionIds) => {
        try {
            const key = `collapsedSections_${listId}`;
            const data = {
                completed,
                activeSectionIds: Array.from(activeSectionIds)
            };
            localStorage.setItem(key, JSON.stringify(data));
        } catch (error) {
            console.error('Error saving collapsed sections to localStorage:', error);
        }
    }, []);

    // Initialize collapsed section states from localStorage
    const [completedSectionCollapsed, setCompletedSectionCollapsed] = useState(() => {
        try {
            const saved = localStorage.getItem('lastSelectedListId');
            if (saved) {
                const listId = parseInt(saved, 10);
                const key = `collapsedSections_${listId}`;
                const savedState = localStorage.getItem(key);
                if (savedState) {
                    const data = JSON.parse(savedState);
                    return data.completed || false;
                }
            }
        } catch (error) {
            console.error('Error initializing completedSectionCollapsed from localStorage:', error);
        }
        return false;
    });
    
    const [activeSectionCollapsed, setActiveSectionCollapsed] = useState(false);
    
    const [collapsedActiveSectionIds, setCollapsedActiveSectionIds] = useState(() => {
        try {
            const saved = localStorage.getItem('lastSelectedListId');
            if (saved) {
                const listId = parseInt(saved, 10);
                const key = `collapsedSections_${listId}`;
                const savedState = localStorage.getItem(key);
                if (savedState) {
                    const data = JSON.parse(savedState);
                    return new Set(data.activeSectionIds || []);
                }
            }
        } catch (error) {
            console.error('Error initializing collapsedActiveSectionIds from localStorage:', error);
        }
        return new Set();
    });

    // Reset multi-select when switching lists
    useEffect(() => {
        clearTaskSelection();
        setIsMultiSelectMode(false);
    }, [selectedListId, clearTaskSelection]);

    // Long-press section actions (mobile)
    const [sectionActionsMenu, setSectionActionsMenu] = useState({
        open: false,
        x: 0,
        y: 0,
        listId: null,
        section: null,
    });
    const sectionActionsMenuRef = useRef(null);
    const sectionLongPressTimerRef = useRef(null);
    const sectionLongPressStartRef = useRef({ x: 0, y: 0, didMove: false });

    // Long-press task actions (mobile)
    const [taskActionsMenu, setTaskActionsMenu] = useState({
        open: false,
        x: 0,
        y: 0,
        listId: null,
        task: null,
    });
    const taskActionsMenuRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const longPressStartRef = useRef({ x: 0, y: 0, didMove: false });

    // Double tap tracking for tasks
    const lastTapTimeRef = useRef(0);
    const lastTapTargetRef = useRef(null);
    const DOUBLE_TAP_DELAY = 300; // milliseconds
    const LONG_PRESS_DELAY = 550; // milliseconds

        // Close the actions menu on outside click / Escape.
    useEffect(() => {
        if (!actionsMenuOpen) return;

        const onPointerDown = (e) => {
            const root = actionsMenuRef.current;
            if (!root) return;
            if (!root.contains(e.target)) {
                setActionsMenuOpen(false);
            }
        };

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setActionsMenuOpen(false);
            }
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [actionsMenuOpen]);

    // Close the task long-press menu on outside click / Escape.
    useEffect(() => {
        if (!taskActionsMenu.open) return;

        // Clear any text selection when menu opens
        if (window.getSelection) {
            const selection = window.getSelection();
            if (selection) {
                selection.removeAllRanges();
            }
        }

        const onPointerDown = (e) => {
            const root = taskActionsMenuRef.current;
            if (!root) return;
            if (!root.contains(e.target)) {
                setTaskActionsMenu((prev) => ({ ...prev, open: false }));
            }
        };

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setTaskActionsMenu((prev) => ({ ...prev, open: false }));
            }
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [taskActionsMenu.open]);

    // Close the section long-press menu on outside click / Escape.
    useEffect(() => {
        if (!sectionActionsMenu.open) return;

        const onPointerDown = (e) => {
            const root = sectionActionsMenuRef.current;
            if (!root) return;
            if (!root.contains(e.target)) {
                setSectionActionsMenu((prev) => ({ ...prev, open: false }));
            }
        };

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setSectionActionsMenu((prev) => ({ ...prev, open: false }));
            }
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [sectionActionsMenu.open]);

    // Close the bulk move menu on outside click / Escape.
    useEffect(() => {
        if (!bulkMoveMenu.open) return;

        const onPointerDown = (e) => {
            const root = bulkMoveMenuRef.current;
            if (!root) return;
            if (!root.contains(e.target)) {
                setBulkMoveMenu((prev) => ({ ...prev, open: false }));
            }
        };

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setBulkMoveMenu((prev) => ({ ...prev, open: false }));
            }
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [bulkMoveMenu.open]);

    // Close the bulk delete menu on outside click / Escape.
    useEffect(() => {
        if (!bulkDeleteMenu.open) return;

        const onPointerDown = (e) => {
            const root = bulkDeleteMenuRef.current;
            if (!root) return;
            if (!root.contains(e.target)) {
                setBulkDeleteMenu((prev) => ({ ...prev, open: false }));
            }
        };

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setBulkDeleteMenu((prev) => ({ ...prev, open: false }));
            }
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [bulkDeleteMenu.open]);
    
    // Track recent local changes to prevent double-updates from real-time events
    const [recentLocalChanges, setRecentLocalChanges] = useState(new Set());

    // Real-time updates handler
    const handleRealTimeUpdate = useCallback((update) => {
        
        switch (update.type) {
            case 'LIST_CREATED':
                // Add new list to the state if user is the owner
                const newList = update.data;
                if (user && newList.owner_id === user.id) {
                    setLists(prev => {
                        // Check if list already exists to avoid duplicates
                        const exists = prev.some(list => list.id === newList.id);
                        if (!exists) {
                            return [...prev, { ...newList, tasks: [] }];
                        }
                        return prev;
                    });
                }
                break;
                
            case 'TASK_CREATED':
                // Add new task to the appropriate list and apply sorting
                setLists(prev => prev.map(list => {
                    if (list.id !== update.data.listId) return list;
                    
                    const newTask = update.data.task;
                    let updatedTasks = [...list.tasks, newTask];
                    
                    // Apply sorting based on list's task_order_type
                    const orderType = list.task_order_type || 'custom';
                    const activeTasks = updatedTasks.filter(t => !t.completed);
                    const completedTasks = updatedTasks.filter(t => t.completed);
                    
                    let sortedActiveTasks = [...activeTasks];
                    switch (orderType) {
                        case 'alphabetical_asc':
                            sortedActiveTasks.sort((a, b) => 
                                a.text.toLowerCase().localeCompare(b.text.toLowerCase())
                            );
                            break;
                        case 'alphabetical_desc':
                            sortedActiveTasks.sort((a, b) => 
                                b.text.toLowerCase().localeCompare(a.text.toLowerCase())
                            );
                            break;
                        case 'date_asc':
                            sortedActiveTasks.sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return aDate - bDate;
                            });
                            break;
                        case 'date_desc':
                            sortedActiveTasks.sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return bDate - aDate;
                            });
                            break;
                        case 'custom':
                        default:
                            sortedActiveTasks.sort((a, b) => (a.order_position || 0) - (b.order_position || 0));
                            break;
                    }
                    
                    return {
                        ...list,
                        tasks: [...sortedActiveTasks, ...completedTasks]
                    };
                }));
                break;
                
            case 'TASK_UPDATED':
                // Check if this is a recent local change to avoid double-updates
                const changeKey = `task_${update.data.task.id}_${update.data.task.completed}`;
                if (recentLocalChanges.has(changeKey)) {
                    return;
                }
                
                // Update the specific task and apply sorting
                setLists(prev => prev.map(list => {
                    if (list.id !== update.data.listId) return list;
                    
                    // Update the task
                    let updatedTasks = list.tasks.map(task => 
                        task.id === update.data.task.id 
                            ? update.data.task 
                            : task
                    );
                    
                    // Apply sorting based on list's task_order_type
                    const orderType = list.task_order_type || 'custom';
                    const activeTasks = updatedTasks.filter(t => !t.completed);
                    const completedTasks = updatedTasks.filter(t => t.completed);
                    
                    let sortedActiveTasks = [...activeTasks];
                    switch (orderType) {
                        case 'alphabetical_asc':
                            sortedActiveTasks.sort((a, b) => 
                                a.text.toLowerCase().localeCompare(b.text.toLowerCase())
                            );
                            break;
                        case 'alphabetical_desc':
                            sortedActiveTasks.sort((a, b) => 
                                b.text.toLowerCase().localeCompare(a.text.toLowerCase())
                            );
                            break;
                        case 'date_asc':
                            sortedActiveTasks.sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return aDate - bDate;
                            });
                            break;
                        case 'date_desc':
                            sortedActiveTasks.sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return bDate - aDate;
                            });
                            break;
                        case 'custom':
                        default:
                            sortedActiveTasks.sort((a, b) => (a.order_position || 0) - (b.order_position || 0));
                            break;
                    }
                    
                    return {
                        ...list,
                        tasks: [...sortedActiveTasks, ...completedTasks]
                    };
                }));
                break;
                
            case 'TASK_DELETED':
                // Remove the deleted task from the appropriate list
                setLists(prev => prev.map(list => 
                    list.id === update.data.listId
                        ? {
                            ...list,
                            tasks: list.tasks.filter(task => task.id !== update.data.taskId)
                        }
                        : list
                ));
                break;

            case 'BULK_TASKS_MOVED':
                // Apply a batch move update without spamming per-task updates.
                setLists(prev => prev.map(list => {
                    if (list.id !== update.data.listId) return list;

                    const movedById = new Map((update.data.tasks || []).map(t => [t.id, t]));

                    // Update tasks with moved data
                    let updatedTasks = (list.tasks || []).map(task => {
                        const moved = movedById.get(task.id);
                        return moved ? { ...task, ...moved } : task;
                    });

                    // Sort tasks based on list's sorting rule
                    const orderType = list.task_order_type || 'alphabetical_asc';
                    const activeTasks = updatedTasks.filter(t => !t.completed);
                    const completedTasks = updatedTasks.filter(t => t.completed);

                    let sortedActiveTasks = [...activeTasks];
                    switch (orderType) {
                        case 'alphabetical_asc':
                            sortedActiveTasks.sort((a, b) => 
                                a.text.toLowerCase().localeCompare(b.text.toLowerCase())
                            );
                            break;
                        case 'alphabetical_desc':
                            sortedActiveTasks.sort((a, b) => 
                                b.text.toLowerCase().localeCompare(a.text.toLowerCase())
                            );
                            break;
                        case 'date_asc':
                            // Sort by most recent date (created_at or updated_at), oldest first
                            sortedActiveTasks.sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return aDate - bDate;
                            });
                            break;
                        case 'date_desc':
                            // Sort by most recent date (created_at or updated_at), newest first
                            sortedActiveTasks.sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return bDate - aDate;
                            });
                            break;
                        case 'custom':
                            // Keep order_position for custom sorting
                            sortedActiveTasks.sort((a, b) => (a.order_position || 0) - (b.order_position || 0));
                            break;
                        default:
                            // Default to alphabetical ascending
                            sortedActiveTasks.sort((a, b) => 
                                a.text.toLowerCase().localeCompare(b.text.toLowerCase())
                            );
                    }

                    return {
                        ...list,
                        tasks: [...sortedActiveTasks, ...completedTasks]
                    };
                }));
                break;

            case 'BULK_TASKS_DELETED':
                // Apply a batch delete update
                setLists(prev => prev.map(list => {
                    if (list.id !== update.data.listId) return list;

                    const deletedIdSet = new Set(update.data.taskIds || []);

                    return {
                        ...list,
                        tasks: (list.tasks || []).filter(task => !deletedIdSet.has(task.id))
                    };
                }));
                break;

            case 'BULK_TASKS_UNCOMPLETED':
                // Apply a batch uncomplete update
                setLists(prev => prev.map(list => {
                    if (list.id !== update.data.listId) return list;

                    const uncompletedById = new Map((update.data.tasks || []).map(t => [t.id, t]));

                    return {
                        ...list,
                        tasks: (list.tasks || []).map(task => {
                            const uncompleted = uncompletedById.get(task.id);
                            return uncompleted ? { ...task, ...uncompleted } : task;
                        })
                    };
                }));
                break;


                
            case 'LIST_UPDATED':
                // Handle list updates - only owners can see updates
                const updatedList = update.data.list;
                
                setLists(prev => {
                    // Check if the current user is the owner
                    const canSeeList = user && updatedList.owner_id === user.id;
                    
                    const existingIndex = prev.findIndex(list => list.id === updatedList.id);
                    
                    if (canSeeList) {
                        if (existingIndex >= 0) {
                            // User is owner and list exists - update it
                            return prev.map(list => 
                                list.id === updatedList.id
                                    ? { 
                                        ...list, 
                                        ...updatedList,
                                        tasks: list.tasks // Keep existing tasks
                                    }
                                    : list
                            );
                        } else {
                            // List became visible (e.g., made public) - add it
                            return [...prev, { ...updatedList, tasks: [] }];
                        }
                    } else {
                        if (existingIndex >= 0) {
                            // User can no longer see the list (e.g., it became private) - remove it
                            return prev.filter(list => list.id !== updatedList.id);
                        } else {
                            // List wasn't visible and still isn't - no change
                            return prev;
                        }
                    }
                });
                break;
                
            case 'LIST_DELETED':
                // Remove the deleted list from the state
                setLists(prev => prev.filter(list => list.id !== update.data.listId));
                break;
                
            case 'LIST_CLEARED':
                // Clear all tasks from the specified list
                setLists(prev => prev.map(list => 
                    list.id === update.data.listId
                        ? { ...list, tasks: [] }
                        : list
                ));
                break;

            case 'ACTIVE_SECTION_CREATED':
                setLists((prev) => prev.map((list) => {
                    if (list.id !== update.data.listId) return list;
                    const existing = (list.active_sections || []).some((s) => s.id === update.data.section.id);
                    if (existing) return list;
                    return { ...list, active_sections: [...(list.active_sections || []), update.data.section] };
                }));
                break;

            case 'ACTIVE_SECTION_UPDATED':
                setLists((prev) => prev.map((list) =>
                    list.id === update.data.listId
                        ? {
                            ...list,
                            active_sections: (list.active_sections || []).map((s) =>
                                s.id === update.data.section.id ? update.data.section : s
                            )
                        }
                        : list
                ));
                break;

            case 'ACTIVE_SECTION_DELETED':
                setLists((prev) => prev.map((list) => {
                    if (list.id !== update.data.listId) return list;

                    const remainingSections = (list.active_sections || []).filter((s) => s.id !== update.data.sectionId);
                    const updatedTasks = (list.tasks || []).map((t) => {
                        if (t.completed) return t;
                        if (t.active_section_id === update.data.sectionId) {
                            return { ...t, active_section_id: update.data.reassignedTo };
                        }
                        return t;
                    });

                    return { ...list, active_sections: remainingSections, tasks: updatedTasks };
                }));
                break;

            case 'ACTIVE_SECTIONS_RESET':
                setLists((prev) => prev.map((list) => {
                    if (list.id !== update.data.listId) return list;

                    const kept = update.data.keptSection;
                    const keptId = kept?.id;

                    const nextSections = keptId
                        ? [kept]
                        : [];

                    // Tasks were cleared already; keep tasks as-is if any mismatch.
                    return {
                        ...list,
                        active_sections: nextSections,
                    };
                }));
                break;
                
            default:
                // Unknown update type
        }
    }, [user, recentLocalChanges]);
    
    // Initialize real-time connection with userId
    useRealTimeUpdates(handleRealTimeUpdate, user?.id);

    const fetchLists = useCallback(async () => {
        try {
            setLoading(true);
            const url = user ? `${API_URL}/api/lists?userId=${user.id}` : `${API_URL}/api/lists`;
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                setLists(data);
            } else {
                console.error('Failed to fetch lists:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Error fetching lists:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Fetch lists from API
    useEffect(() => {
        fetchLists();
    }, [fetchLists]); // Now properly depends on the memoized fetchLists function

    // Helper function to get the currently selected list
    const getSelectedList = () => {
        return lists.find(list => list.id === selectedListId);
    };

    const getDefaultActiveSectionId = (list) => {
        const sections = list?.active_sections || [];
        return sections.length > 0 ? sections[0].id : null;
    };

    // Load section collapse state from localStorage when switching lists.
    // IMPORTANT: do not depend on `selectedList` (derived from `lists`), otherwise any
    // list updates (like adding a task) will re-run the effect and expand sections.
    useEffect(() => {
        if (!selectedListId) return;
        
        // Load collapsed state from localStorage
        const savedState = loadCollapsedSections(selectedListId);
        setCollapsedActiveSectionIds(savedState.activeSectionIds);
        setActiveSectionCollapsed(false);
        setCompletedSectionCollapsed(savedState.completed);
    }, [selectedListId, loadCollapsedSections]);

    // Save collapsed state to localStorage whenever it changes
    useEffect(() => {
        if (!selectedListId) return;
        saveCollapsedSections(selectedListId, completedSectionCollapsed, collapsedActiveSectionIds);
    }, [selectedListId, completedSectionCollapsed, collapsedActiveSectionIds, saveCollapsedSections]);

    const toggleActiveSectionCollapsed = (sectionId) => {
        setCollapsedActiveSectionIds((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId);
            else next.add(sectionId);
            return next;
        });
    };

    const openTaskActionsMenuAtEvent = (e, listId, task) => {
        // Disable task actions menu when in move or delete mode
        if (isMultiSelectMode || isDeleteMode) return;
        
        // Prefer touch coords; fallback to mouse/pointer
        const touch = e?.touches?.[0] || e?.changedTouches?.[0];
        const clientX = touch ? touch.clientX : (e?.clientX ?? 0);
        const clientY = touch ? touch.clientY : (e?.clientY ?? 0);

        setTaskActionsMenu({
            open: true,
            x: clientX,
            y: clientY,
            listId,
            task,
        });
    };

    const openSectionActionsMenuAtEvent = (e, listId, section) => {
        // Disable section actions menu when in move or delete mode
        if (isMultiSelectMode || isDeleteMode) return;
        
        const touch = e?.touches?.[0] || e?.changedTouches?.[0];
        const clientX = touch ? touch.clientX : (e?.clientX ?? 0);
        const clientY = touch ? touch.clientY : (e?.clientY ?? 0);

        setSectionActionsMenu({
            open: true,
            x: clientX,
            y: clientY,
            listId,
            section,
        });
    };

    const moveTaskToSection = async (listId, task, sectionId) => {
        try {
            const response = await fetch(`${API_URL}/api/tasks/${task.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ active_section_id: sectionId }),
            });

            if (!response.ok) {
                console.error('Failed to move task section:', response.status, response.statusText);
                alert('Failed to move task.');
                return;
            }

            const updatedTask = await response.json();
            setLists((prev) => prev.map((l) =>
                l.id === listId
                    ? {
                        ...l,
                        tasks: (l.tasks || []).map((t) => t.id === updatedTask.id ? { ...t, ...updatedTask } : t)
                    }
                    : l
            ));
        } catch (err) {
            console.error('Error moving task section:', err);
            alert('Error moving task.');
        }
    };

    const createActiveSection = async (listId) => {
        // Open the modal instead of using window.prompt
        openAddSectionModal(listId);
    };

    // Function to handle the actual section creation (called from modal submit)
    const handleAddSectionSubmit = async () => {
        if (!addSectionListId || !newSectionTitle.trim()) return;

        try {
            const response = await fetch(`${API_URL}/api/lists/${addSectionListId}/active-sections`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: newSectionTitle.trim() }),
            });

            if (!response.ok) {
                console.error('Failed to create active section:', response.status, response.statusText);
                alert('Failed to create section.');
                return;
            }

            const section = await response.json();
            setLists((prev) => prev.map((l) => {
                if (l.id !== addSectionListId) return l;
                const exists = (l.active_sections || []).some((s) => s.id === section.id);
                if (exists) return l;
                return { ...l, active_sections: [...(l.active_sections || []), section] };
            }));

            // Close the modal
            closeAddSectionModal();
        } catch (err) {
            console.error('Error creating active section:', err);
            alert('Error creating section.');
        }
    };

    const ensureNotSectionedSection = async (listId) => {
        const list = lists.find((l) => l.id === listId);
        if (!list) return null;

        const existing = (list.active_sections || []).find(
            (s) => (s.title || '').trim().toLowerCase() === 'not sectioned'
        );
        if (existing) return existing;

        try {
            const response = await fetch(`${API_URL}/api/lists/${listId}/active-sections`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: 'Not Sectioned' }),
            });

            if (!response.ok) {
                console.error('Failed to create Not Sectioned section:', response.status, response.statusText);
                alert('Failed to create "Not Sectioned" section.');
                return null;
            }

            const section = await response.json();
            setLists((prev) => prev.map((l) => {
                if (l.id !== listId) return l;
                const already = (l.active_sections || []).some((s) => s.id === section.id);
                if (already) return l;
                return { ...l, active_sections: [...(l.active_sections || []), section] };
            }));
            return section;
        } catch (err) {
            console.error('Error creating Not Sectioned section:', err);
            alert('Error creating "Not Sectioned" section.');
            return null;
        }
    };

    const renameActiveSection = async (section) => {
        // Open the modal instead of using window.prompt
        openRenameSectionModal(section);
    };

    // Function to handle the actual section rename (called from modal submit)
    const handleRenameSectionSubmit = async () => {
        if (!renamingSection || !renameSectionTitle.trim()) return;

        try {
            const response = await fetch(`${API_URL}/api/active-sections/${renamingSection.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: renameSectionTitle.trim() }),
            });

            if (!response.ok) {
                console.error('Failed to rename active section:', response.status, response.statusText);
                alert('Failed to rename section.');
                return;
            }

            const updated = await response.json();
            setLists((prev) => prev.map((l) =>
                l.id === updated.list_id
                    ? {
                        ...l,
                        active_sections: (l.active_sections || []).map((s) => s.id === updated.id ? updated : s)
                    }
                    : l
            ));

            // Close the modal
            closeRenameSectionModal();
        } catch (err) {
            console.error('Error renaming active section:', err);
            alert('Error renaming section.');
        }
    };

    const deleteActiveSection = async (section) => {
        try {
            const list = getSelectedList();
            if (!list) return;

            const ok = window.confirm(`Delete section "${section.title}"? Tasks in it will be moved to "Not Sectioned".`);
            if (!ok) return;

            // If the section still contains active tasks, ensure the fallback section exists first.
            // This guarantees we always have a valid target to move tasks into.
            const hasActiveTasksInSection = (list.tasks || []).some(
                (t) => !t.completed && t.active_section_id === section.id
            );

            if (hasActiveTasksInSection) {
                await ensureNotSectionedSection(list.id);
            }

            const response = await fetch(`${API_URL}/api/active-sections/${section.id}`, {
                method: 'DELETE'
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                alert(data?.error || 'Failed to delete section.');
                return;
            }

            setLists((prev) => prev.map((l) => {
                if (l.id !== data.listId) return l;
                const remainingSections = (l.active_sections || []).filter((s) => s.id !== data.sectionId);
                const updatedTasks = (l.tasks || []).map((t) => {
                    if (t.completed) return t;
                    if (t.active_section_id === data.sectionId) {
                        return { ...t, active_section_id: data.reassignedTo };
                    }
                    return t;
                });
                return { ...l, active_sections: remainingSections, tasks: updatedTasks };
            }));
        } catch (err) {
            console.error('Error deleting active section:', err);
            alert('Error deleting section.');
        }
    };

    // Save selectedListId to localStorage whenever it changes
    useEffect(() => {
        if (selectedListId !== null) {
            try {
                localStorage.setItem('lastSelectedListId', selectedListId.toString());
            } catch (error) {
                console.error('Error saving lastSelectedListId to localStorage:', error);
            }
        }
    }, [selectedListId]);

    // Auto-select list when lists load
    useEffect(() => {
        if (lists.length === 0) return;
        
        // If we have a selectedListId, verify it still exists in the lists
        if (selectedListId) {
            const listExists = lists.some(list => list.id === selectedListId);
            if (listExists) {
                // The saved list still exists, keep it selected
                return;
            }
            // The saved list no longer exists, fall through to select first list
        }
        
        // No valid selected list, select the first one
        setSelectedListId(lists[0].id);
    }, [lists, selectedListId]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownOpen && !event.target.closest('.dropdown')) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Apply dark theme on mount and listen for changes
    useEffect(() => {
        const applyDarkTheme = () => {
            const isDarkTheme = localStorage.getItem('darkTheme') === 'true';
            if (isDarkTheme) {
                document.body.classList.add('dark-theme');
            } else {
                document.body.classList.remove('dark-theme');
            }
        };

        // Apply on mount
        applyDarkTheme();

        // Listen for storage changes (when toggled from admin page)
        window.addEventListener('storage', applyDarkTheme);
        
        return () => {
            window.removeEventListener('storage', applyDarkTheme);
        };
    }, []);

    const handleCreateList = async (e) => {
        e.preventDefault();
        if (!newListName.trim()) return;

        try {
            const response = await fetch(`${API_URL}/api/lists`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: newListName.trim(),
                    description: newListDescription.trim(),
                    active_header: editingActiveHeader.trim() || (newListType === 'simple' ? 'Items' : 'Active Tasks'),
                    completed_header: editingCompletedHeader.trim() || 'Completed Tasks',
                    owner_id: user ? user.id : null, // Automatically set creator as owner
                    task_order_type: newListTaskOrder,
                    list_type: newListType // Add list type to the request
                }),
            });

            if (response.ok) {
                const newList = await response.json();
                // List creation will be handled by real-time updates
                console.log('✅ List created successfully:', newList.name);

                // Ensure default sections exist for the new list (and any existing lists).
                // This keeps the UI consistent even if the server doesn't auto-create them on list creation.
                try {
                    await fetch(`${API_URL}/api/db/ensure-active-sections`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });
                } catch (err) {
                    console.warn('Could not ensure active sections after list creation:', err);
                }

                // Refresh lists so the new list shows its default sections immediately.
                await fetchLists();

                // Close the modal
                closeCreateListModal();

                // Immediately select the newly created list so the user lands in it
                if (newList?.id) {
                    setSelectedListId(newList.id);
                    
                    // Scroll to top after modal closes and state updates
                    setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }, 150);
                }
            } else {
                console.error('Failed to create list:', response.status, response.statusText);
                alert('Failed to create list. Please try again.');
            }
        } catch (error) {
            console.error('Error creating list:', error);
            alert('Error creating list. Please try again.');
        }
    };

    const [lockingListId, setLockingListId] = useState(null);

    const handleToggleLock = async (listId, currentLocked) => {
        // Get the list name for better confirmation message
        const list = lists.find(l => l.id === listId);
        const listName = list ? list.name : 'this list';
        
        // Show confirmation dialog
        const confirmMessage = currentLocked 
            ? `Are you sure you want to unlock "${listName}"?`
            : `Are you sure you want to lock "${listName}"?`;
        
        if (!window.confirm(confirmMessage)) {
            return; // User cancelled
        }

        setLockingListId(listId);
        try {
            const response = await fetch(`${API_URL}/api/lists/${listId}/lock`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ locked: !currentLocked }),
            });

            if (response.ok) {
                // Refresh the lists to get updated lock status
                fetchLists();
            } else {
                const errorData = await response.json();
                console.error('Failed to toggle lock:', response.status, response.statusText, errorData);
                alert(`Failed to ${!currentLocked ? 'lock' : 'unlock'} list: ${errorData.error || 'Please try again.'}`);
            }
        } catch (error) {
            console.error('Error toggling lock:', error);
            alert(`Failed to ${!currentLocked ? 'lock' : 'unlock'} list. Please try again.`);
        } finally {
            setLockingListId(null);
        }
    };

    const handleAddUserSubmit = async () => {
        const username = addUserUsername.trim();
        
        if (!username || !addUserListId || !user) {
            return;
        }

        try {
            // First, get the user ID from the username
            const userResponse = await fetch(`${API_URL}/api/users/by-username/${encodeURIComponent(username)}`);
            
            if (!userResponse.ok) {
                if (userResponse.status === 404) {
                    alert(`User "${username}" not found. Please check the username and try again.`);
                } else {
                    alert('Failed to find user. Please try again.');
                }
                return;
            }
            
            const userData = await userResponse.json();
            
            // Check if trying to add themselves
            if (userData.id === user.id) {
                alert('You already have access to this list as the owner.');
                return;
            }
            
            // Grant access
            const grantResponse = await fetch(`${API_URL}/api/lists/${addUserListId}/access`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: userData.id,
                    grantedBy: user.id
                }),
            });

            if (grantResponse.ok) {
                console.log(`✅ Granted access to user ${username}`);
                alert(`Access granted to ${username} successfully!`);
                closeAddUserModal();
            } else {
                const errorData = await grantResponse.json();
                console.error('Failed to grant access:', errorData);
                alert(`Failed to grant access: ${errorData.error || 'Please try again.'}`);
            }
        } catch (error) {
            console.error('Error granting access:', error);
            alert('Failed to grant access. Please try again.');
        }
    };

    const handleRemoveUser = async (listId, userId, username) => {
        if (!user) return;

        // Confirm removal
        if (!window.confirm(`Are you sure you want to remove ${username} from this list?`)) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/lists/${listId}/access/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    revokedBy: user.id
                }),
            });

            if (response.ok) {
                console.log(`✅ Removed user ${username} from list`);
                alert(`${username} has been removed from the list.`);
                
                // Refresh the user list in the modal
                if (showManageUsersModal && manageUsersListId === listId) {
                    openManageUsersModal(listId);
                }
            } else {
                const errorData = await response.json();
                console.error('Failed to remove user:', errorData);
                alert(`Failed to remove user: ${errorData.error || 'Please try again.'}`);
            }
        } catch (error) {
            console.error('Error removing user:', error);
            alert('Failed to remove user. Please try again.');
        }
    };

    const handleTaskOrderChange = async (listId, newOrderType) => {
        try {
            const response = await fetch(`${API_URL}/api/lists/${listId}/task-order`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    task_order_type: newOrderType,
                    userId: user ? user.id : null
                }),
            });

            if (response.ok) {
                // Refresh the lists to get updated task ordering
                fetchLists();
            } else {
                const errorData = await response.json();
                console.error('Failed to update task ordering:', response.status, response.statusText, errorData);
                alert(`Failed to update task ordering: ${errorData.error || 'Please try again.'}`);
            }
        } catch (error) {
            console.error('Error updating task ordering:', error);
            alert('Failed to update task ordering. Please try again.');
        }
    };

    const handleListSelection = async (listId) => {
        if (!user) {
            setSelectedListId(listId);
            setDropdownOpen(false);
            return;
        }

        try {
            // Verify the user still has access to this list by fetching all lists
            const response = await fetch(`${API_URL}/api/lists?userId=${user.id}`);
            
            if (response.ok) {
                const userLists = await response.json();
                const hasAccess = userLists.some(list => list.id === listId);
                
                if (hasAccess) {
                    // User has access, select the list
                    setSelectedListId(listId);
                    setDropdownOpen(false);
                } else {
                    // User doesn't have access anymore
                    alert('You no longer have access to this list. It will be removed from your dropdown.');
                    
                    // Update local lists to match server state
                    setLists(userLists);
                    
                    // If this was the selected list, select the first available list
                    if (selectedListId === listId) {
                        if (userLists.length > 0) {
                            setSelectedListId(userLists[0].id);
                        } else {
                            setSelectedListId(null);
                        }
                    }
                    
                    setDropdownOpen(false);
                }
            } else {
                console.error('Error checking list access:', response.status);
                alert('Error checking list access. Please try again.');
                setDropdownOpen(false);
            }
        } catch (error) {
            console.error('Error verifying list access:', error);
            alert('Error verifying list access. Please try again.');
            setDropdownOpen(false);
        }
    };

    const handleDeleteList = async (listId) => {
        if (window.confirm('Are you sure you want to delete this list?')) {
            try {
                const response = await fetch(`${API_URL}/api/lists/${listId}`, {
                    method: 'DELETE',
                });

                if (response.ok) {
                    console.log('✅ List deleted successfully');
                    
                    // If the deleted list was selected, navigate to the first available list
                    if (selectedListId === listId) {
                        // Find the first list that isn't being deleted
                        const firstList = lists.find(list => list.id !== listId);
                        if (firstList) {
                            setSelectedListId(firstList.id);
                        } else {
                            setSelectedListId(null);
                        }
                    }
                    
                    // Refresh the lists to remove the deleted list from the dropdown
                    await fetchLists();
                } else {
                    const errorData = await response.json();
                    console.error('Failed to delete list:', response.status, response.statusText);
                    if (response.status === 400 && errorData.error?.includes('locked')) {
                        alert('Cannot delete a locked list. Please unlock it first.');
                    } else {
                        alert(`Failed to delete list: ${errorData.error || 'Please try again.'}`);
                    }
                }
            } catch (error) {
                console.error('Error deleting list:', error);
                alert('Error deleting list. Please try again.');
            }
        }
    };

    const handleClearList = async (listId) => {
        // Find the list to get its name for confirmation
        const list = lists.find(l => l.id === listId);
        const listName = list ? list.name : 'this list';
        const taskCount = list ? list.tasks.length : 0;
        
        if (taskCount === 0) {
            alert('This list has no tasks to clear.');
            return;
        }
        
        const confirmMessage = `Are you sure you want to clear ALL ${taskCount} tasks from "${listName}"? This action cannot be undone.`;
        
        if (window.confirm(confirmMessage)) {
            try {
                const response = await fetch(`${API_URL}/api/lists/${listId}/tasks`, {
                    method: 'DELETE',
                });

                if (response.ok) {
                    const data = await response.json();
                    // Update local state to remove all tasks from the list
                    setLists(prev => prev.map(list => 
                        list.id === listId 
                            ? { ...list, tasks: [] }
                            : list
                    ));
                    alert(`Successfully cleared ${data.deletedCount} tasks from "${listName}".`);
                } else {
                    const errorData = await response.json();
                    console.error('Failed to clear list:', response.status, response.statusText);
                    alert(`Failed to clear list: ${errorData.error || 'Please try again.'}`);
                }
            } catch (error) {
                console.error('Error clearing list:', error);
                alert('Error clearing list. Please try again.');
            }
        }
    };

    const handleResetSections = async (listId) => {
        const list = lists.find(l => l.id === listId);
        const listName = list ? list.name : 'this list';
        
        if (window.confirm(`Reset all tasks in "${listName}" to the Not Sectioned section and mark them as active?`)) {
            try {
                // Get all tasks (both active and completed)
                const allTasks = list.tasks;
                const completedTasks = allTasks.filter(t => t.completed);
                
                // Find the "Not Sectioned" section or use the first section as default
                const activeSections = list.active_sections || [];
                const notSectionedSection = activeSections.find(s => 
                    (s.title || '').trim().toLowerCase() === 'not sectioned'
                );
                const targetSectionId = notSectionedSection?.id || (activeSections[0]?.id ?? null);

                console.log('Reset Sections Debug:', {
                    listId,
                    taskCount: allTasks.length,
                    completedCount: completedTasks.length,
                    activeSections: activeSections.map(s => ({ id: s.id, title: s.title })),
                    notSectionedSection,
                    targetSectionId,
                    taskIds: allTasks.map(t => t.id)
                });

                if (allTasks.length === 0) {
                    alert('No tasks to reset.');
                    return;
                }

                if (!targetSectionId) {
                    alert('No sections available to reset to.');
                    return;
                }

                const taskIds = allTasks.map(t => t.id);

                // Step 1: Uncomplete all completed tasks
                if (completedTasks.length > 0) {
                    const uncompleteResponse = await fetch(`${API_URL}/api/tasks/bulk-uncomplete`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            listId,
                            taskIds: completedTasks.map(t => t.id),
                        }),
                    });

                    if (!uncompleteResponse.ok) {
                        const errorData = await uncompleteResponse.json();
                        console.error('Failed to uncomplete tasks:', uncompleteResponse.status, uncompleteResponse.statusText, errorData);
                        alert(`Failed to uncomplete tasks: ${errorData.error || 'Please try again.'}`);
                        return;
                    }

                    console.log('✅ Uncompleted tasks successfully');
                }

                // Step 2: Move all tasks to the target section
                const moveResponse = await fetch(`${API_URL}/api/tasks/bulk-move`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        listId,
                        taskIds,
                        targetActiveSectionId: targetSectionId,
                    }),
                });

                if (moveResponse.ok) {
                    const responseData = await moveResponse.json();
                    console.log('✅ All tasks reset to Not Sectioned successfully', responseData);
                } else {
                    const errorData = await moveResponse.json();
                    console.error('Failed to reset sections:', moveResponse.status, moveResponse.statusText, errorData);
                    alert(`Failed to reset sections: ${errorData.error || 'Please try again.'}`);
                }
            } catch (error) {
                console.error('Error resetting sections:', error);
                alert('Error resetting sections. Please try again.');
            }
        }
    };

    const handleEditList = (list) => {
        setEditingList(list);
        setNewListName(list.name);
        setNewListDescription(list.description);
        setEditingActiveHeader(list.active_header || '');
        setEditingCompletedHeader(list.completed_header || '');
        setEditingTaskOrderType(list.task_order_type || 'alphabetical_asc');
        setEditingListType(list.list_type || 'checklist'); // Set the list type for editing
    };

    const handleUpdateList = async (e) => {
        e.preventDefault();
        if (!newListName?.trim()) return;

        try {
            const listTypeChanged = editingListType !== editingList.list_type;
            
            // If list type changed, uncomplete all tasks and move to "Not Sectioned"
            if (listTypeChanged) {
                console.log('🔄 List type changed, preparing tasks...');
                
                // Get "Not Sectioned" section
                const notSectionedSection = editingList.active_sections?.find(
                    s => s.title.toLowerCase() === 'not sectioned'
                );
                
                if (!notSectionedSection) {
                    console.error('❌ Not Sectioned section not found');
                    alert('Error: Could not find "Not Sectioned" section. Please try again.');
                    return;
                }
                
                // Update all tasks: uncomplete (if needed) and move to "Not Sectioned"
                if (editingList.tasks?.length > 0) {
                    const updatePromises = editingList.tasks.map(task => {
                        const updates = {
                            active_section_id: notSectionedSection.id
                        };
                        
                        // If changing to simple, also uncomplete the task
                        if (editingListType === 'simple' && task.completed) {
                            updates.completed = false;
                        }
                        
                        return fetch(`${API_URL}/api/tasks/${task.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(updates)
                        });
                    });
                    
                    await Promise.all(updatePromises);
                    console.log('✅ All tasks moved to Not Sectioned');
                }
                
                // Delete all sections except "Not Sectioned"
                const sectionsToDelete = editingList.active_sections?.filter(
                    s => s.title.toLowerCase() !== 'not sectioned'
                ) || [];
                
                if (sectionsToDelete.length > 0) {
                    const deletePromises = sectionsToDelete.map(section =>
                        fetch(`${API_URL}/api/active-sections/${section.id}`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' }
                        })
                    );
                    
                    await Promise.all(deletePromises);
                    console.log(`✅ Deleted ${sectionsToDelete.length} sections`);
                }
            }
            
            // Update the list details including list_type
            const response = await fetch(`${API_URL}/api/lists/${editingList.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: newListName?.trim() || '',
                    description: newListDescription?.trim() || '',
                    active_header: editingActiveHeader?.trim() || 'Active Tasks',
                    completed_header: editingCompletedHeader?.trim() || 'Completed Tasks',
                    list_type: editingListType // Include list type in update
                }),
            });

            if (response.ok) {
                const updatedList = await response.json();
                
                // Update task ordering if it changed
                if (editingTaskOrderType !== (editingList.task_order_type || 'alphabetical_asc')) {
                    await handleTaskOrderChange(editingList.id, editingTaskOrderType);
                }
                
                // List update will be handled by real-time updates
                console.log('✅ List updated successfully:', updatedList.name);
                
                // If list type changed, refresh the list to show new UI
                if (listTypeChanged) {
                    setTimeout(() => fetchLists(), 500); // Small delay to let updates propagate
                }
                
                setEditingList(null);
                setNewListName('');
                setNewListDescription('');
                setEditingActiveHeader('');
                setEditingCompletedHeader('');
                setEditingTaskOrderType('custom');
                setEditingListType('checklist');
                closeCreateListModal();
            } else {
                console.error('Failed to update list:', response.status, response.statusText);
                alert('Failed to update list. Please try again.');
            }
        } catch (error) {
            console.error('Error updating list:', error);
            alert('Error updating list. Please try again.');
        }
    };

    const toggleTaskComplete = async (listId, taskId) => {
        // Find current task to get its completion status
        const list = lists.find(l => l.id === listId);
        
        // Don't allow completion toggling for simple lists
        if (list?.list_type === 'simple') {
            return;
        }
        
        const task = list?.tasks.find(t => t.id === taskId);
        if (!task) return;

        const newCompletedStatus = !task.completed;
    const orderType = list.task_order_type || 'alphabetical_asc';
        
        try {
            // First, update the task's completion status
            const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    completed: newCompletedStatus
                }),
            });

            if (response.ok) {
                const updatedTask = await response.json();
                
                // Update local state with the completion status change
                let updatedTasks = list.tasks.map(t =>
                    t.id === taskId ? { 
                        ...t, 
                        completed: updatedTask.completed,
                        order_position: updatedTask.order_position || t.order_position
                    } : t
                );
                
                // Handle different ordering types when uncompleting a task
                if (!newCompletedStatus) {
                    // Task is being uncompleted - need to place it correctly
                    const activeTasks = updatedTasks.filter(t => !t.completed && t.id !== taskId);
                    const completedTasks = updatedTasks.filter(t => t.completed);
                    const uncompletedTask = updatedTasks.find(t => t.id === taskId);
                    
                    let orderedActiveTasks;
                    
                    switch (orderType) {
                        case 'alphabetical_asc':
                            // Insert task in alphabetical order A-Z
                            orderedActiveTasks = [...activeTasks, uncompletedTask].sort((a, b) => 
                                a.text.toLowerCase().localeCompare(b.text.toLowerCase())
                            );
                            break;
                            
                        case 'alphabetical_desc':
                            // Insert task in alphabetical order Z-A
                            orderedActiveTasks = [...activeTasks, uncompletedTask].sort((a, b) => 
                                b.text.toLowerCase().localeCompare(a.text.toLowerCase())
                            );
                            break;
                            
                        case 'date_asc':
                            // Sort by most recent date (created_at or updated_at), oldest first
                            orderedActiveTasks = [...activeTasks, uncompletedTask].sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return aDate - bDate;
                            });
                            break;
                            
                        case 'date_desc':
                            // Sort by most recent date (created_at or updated_at), newest first
                            orderedActiveTasks = [...activeTasks, uncompletedTask].sort((a, b) => {
                                const aDate = Math.max(new Date(a.created_at), new Date(a.updated_at));
                                const bDate = Math.max(new Date(b.created_at), new Date(b.updated_at));
                                return bDate - aDate;
                            });
                            break;
                            
                        case 'custom':
                        default:
                            // For custom ordering, add to the bottom of the list
                            orderedActiveTasks = [...activeTasks, uncompletedTask];
                            break;
                    }
                    
                    // Update positions for active tasks
                    const reorderedActiveTasks = orderedActiveTasks.map((task, index) => ({
                        ...task,
                        order_position: index + 1
                    }));
                    
                    updatedTasks = [...reorderedActiveTasks, ...completedTasks];
                    
                    // For custom ordering, update user-specific positions
                    if (orderType === 'custom' && user?.id) {
                        const taskUpdates = reorderedActiveTasks.map((task, index) => ({
                            id: task.id,
                            order_position: index + 1
                        }));
                        
                        const reorderResponse = await fetch(`${API_URL}/api/tasks/reorder`, {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ 
                                listId, 
                                taskUpdates,
                                userId: user.id
                            }),
                        });

                        if (!reorderResponse.ok) {
                            console.error('Failed to update custom task positions:', reorderResponse.status, reorderResponse.statusText);
                            // Revert if reorder fails
                            fetchLists();
                            alert('Failed to update task positions. Please try again.');
                            return;
                        }
                    }
                } else {
                    // Task is being completed - just normalize remaining active tasks
                    const activeTasks = updatedTasks.filter(t => !t.completed);
                    const completedTasks = updatedTasks.filter(t => t.completed);
                    
                    // Normalize active task positions to 1, 2, 3...
                    const normalizedActiveTasks = activeTasks.map((task, index) => ({
                        ...task,
                        order_position: index + 1
                    }));
                    
                    updatedTasks = [...normalizedActiveTasks, ...completedTasks];
                    
                    // Update user-specific positions for custom ordering
                    if (orderType === 'custom' && normalizedActiveTasks.length > 0 && user?.id) {
                        const taskUpdates = normalizedActiveTasks.map((task, index) => ({
                            id: task.id,
                            order_position: index + 1
                        }));
                        
                        const reorderResponse = await fetch(`${API_URL}/api/tasks/reorder`, {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ 
                                listId, 
                                taskUpdates,
                                userId: user.id
                            }),
                        });

                        if (!reorderResponse.ok) {
                            console.error('Failed to normalize task positions:', reorderResponse.status, reorderResponse.statusText);
                            // Revert if reorder fails
                            fetchLists();
                            alert('Failed to update task positions. Please try again.');
                            return;
                        }
                    }
                }
                
                // Track this change to prevent duplicate real-time updates
                const changeKey = `task_${taskId}_${newCompletedStatus}`;
                setRecentLocalChanges(prev => {
                    const newSet = new Set(prev);
                    newSet.add(changeKey);
                    return newSet;
                });
                
                // Remove the change key after 2 seconds
                setTimeout(() => {
                    setRecentLocalChanges(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(changeKey);
                        return newSet;
                    });
                }, 2000);
                
                // Update local state immediately
                setLists(prev => prev.map(l => 
                    l.id === listId 
                        ? { ...l, tasks: updatedTasks }
                        : l
                ));
            } else {
                console.error('Failed to update task:', response.status, response.statusText);
                alert('Failed to update task. Please try again.');
            }
        } catch (error) {
            console.error('Error updating task:', error);
            alert('Error updating task. Please try again.');
        }
    };

    const addTaskToList = async (listId, rawText, note = null) => {
        const taskText = (rawText ?? '').trim();
        if (taskText) {
            try {
                const selectedList = lists.find(l => l.id === listId);
                // Always place new tasks into "Not Sectioned".
                // This keeps "new/inbox" items predictable regardless of the currently selected section.
                const notSectioned = await ensureNotSectionedSection(listId);
                const defaultSectionId = getDefaultActiveSectionId(selectedList);
                const targetSectionId = notSectioned?.id ?? defaultSectionId;

                const response = await fetch(`${API_URL}/api/lists/${listId}/tasks`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        text: taskText,
                        active_section_id: targetSectionId,
                        note: note && note.trim() ? note.trim() : null
                    }),
                });

                if (response.ok) {
                    // Task will be added via real-time update, no need to update state here
                    console.log('Task created successfully');
                } else {
                    console.error('Failed to add task:', response.status, response.statusText);
                    alert('Failed to add task. Please try again.');
                }
            } catch (error) {
                console.error('Error adding task:', error);
                alert('Error adding task. Please try again.');
            }
        }
    };

    const cloneTask = async (listId, task) => {
        try {
            const clonedTaskText = `Clone - ${task.text}`;
            const targetSectionId = task.active_section_id;

            const response = await fetch(`${API_URL}/api/lists/${listId}/tasks`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: clonedTaskText,
                    active_section_id: targetSectionId
                }),
            });

            if (response.ok) {
                console.log('✅ Task cloned successfully');
            } else {
                console.error('Failed to clone task:', response.status, response.statusText);
                alert('Failed to clone task. Please try again.');
            }
        } catch (error) {
            console.error('Error cloning task:', error);
            alert('Error cloning task. Please try again.');
        }
    };

    // Bulk add tasks functions
    const openBulkAddModal = useCallback(() => {
        setShowBulkAddModal(true);
        setBulkAddTaskText('');
        setBulkAddTaskNote('');
    }, []);

    const closeBulkAddModal = useCallback(() => {
        setShowBulkAddModal(false);
        setBulkAddTaskText('');
        setBulkAddTaskNote('');
    }, []);

    const handleBulkAddTask = async (e) => {
        e.preventDefault();
        
        const taskText = bulkAddTaskText.trim();
        if (!taskText || !selectedListId) return;

        try {
            const selectedList = lists.find(l => l.id === selectedListId);
            const notSectionedSection = selectedList?.active_sections?.find(
                s => s.title.toLowerCase() === 'not sectioned'
            );

            const response = await fetch(`${API_URL}/api/lists/${selectedListId}/tasks`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: taskText,
                    active_section_id: notSectionedSection?.id || null,
                    note: bulkAddTaskNote && bulkAddTaskNote.trim() ? bulkAddTaskNote.trim() : null
                }),
            });

            if (response.ok) {
                // Clear the inputs but keep modal open
                setBulkAddTaskText('');
                setBulkAddTaskNote('');
                // Re-focus the input immediately and after a short delay to keep keyboard open
                if (bulkAddInputRef.current) {
                    bulkAddInputRef.current.focus();
                    // Additional focus attempts to ensure keyboard stays up on mobile
                    setTimeout(() => bulkAddInputRef.current?.focus(), 10);
                    setTimeout(() => bulkAddInputRef.current?.focus(), 100);
                }
            } else {
                console.error('Failed to add task:', response.status, response.statusText);
                alert('Failed to add task. Please try again.');
            }
        } catch (error) {
            console.error('Error adding task:', error);
            alert('Error adding task. Please try again.');
        }
    };

    // Auto-focus bulk add input when modal opens
    useEffect(() => {
        if (!showBulkAddModal) return;
        const timer = setTimeout(() => bulkAddInputRef.current?.focus(), 50);
        return () => clearTimeout(timer);
    }, [showBulkAddModal]);

    // Close bulk add modal on Escape key
    useEffect(() => {
        if (!showBulkAddModal) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeBulkAddModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showBulkAddModal, closeBulkAddModal]);

    const deleteTask = async (listId, taskId) => {
        // Find the task to get its text for confirmation
        const list = lists.find(l => l.id === listId);
        const task = list?.tasks.find(t => t.id === taskId);
        
        if (task && window.confirm(`Are you sure you want to delete the task "${task.text}"?`)) {
            try {
                const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
                    method: 'DELETE',
                });

                if (response.ok) {
                    // Task deletion will be handled by real-time updates
                    console.log('✅ Task deleted successfully');
                } else {
                    console.error('Failed to delete task:', response.status, response.statusText);
                    alert('Failed to delete task. Please try again.');
                }
            } catch (error) {
                console.error('Error deleting task:', error);
                alert('Error deleting task. Please try again.');
            }
        }
    };

    // Function to handle the actual task edit (called from modal submit)
    const handleEditTaskSubmit = async () => {
        if (!editingTask || !editingListId) return;

        if (!editTaskText.trim()) {
            alert('Task text cannot be empty.');
            return;
        }

        try {
            // Convert dayjs object to ISO 8601 timestamp or null
            const dueDateValue = editTaskDueDate ? editTaskDueDate.toISOString() : null;
            
            const response = await fetch(`${API_URL}/api/tasks/${editingTask.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: editTaskText.trim(),
                    completed: editingTask.completed,
                    due_date: dueDateValue,
                }),
            });

            if (!response.ok) {
                console.error('Failed to update task:', response.status, response.statusText);
                alert('Failed to update task. Please try again.');
                return;
            }

            const updatedTask = await response.json();

            // Update local state immediately (real-time updates will also reconcile)
            setLists(prev => prev.map(list =>
                list.id === editingListId
                    ? {
                        ...list,
                        tasks: list.tasks.map(t =>
                            t.id === editingTask.id ? { ...t, text: updatedTask.text, due_date: updatedTask.due_date } : t
                        )
                    }
                    : list
            ));

            // Close the modal
            closeEditTaskModal();
        } catch (error) {
            console.error('Error updating task:', error);
            alert('Error updating task. Please try again.');
        }
    };

    // Function to handle note edit (called from modal submit)
    const handleEditNoteSubmit = async () => {
        if (!editingNoteTask || !editingNoteListId) return;

        try {
            const response = await fetch(`${API_URL}/api/tasks/${editingNoteTask.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    note: editNoteText.trim() || null, // Send null if empty to clear note
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update note');
            }

            const updatedTask = await response.json();

            // Update local state immediately (real-time updates will also reconcile)
            setLists(prev => prev.map(list =>
                list.id === editingNoteListId
                    ? {
                        ...list,
                        tasks: list.tasks.map(t =>
                            t.id === editingNoteTask.id ? { ...t, note: updatedTask.note } : t
                        )
                    }
                    : list
            ));

            // Close the modal
            closeEditNoteModal();
        } catch (error) {
            console.error('Error updating note:', error);
            alert('Error updating note. Please try again.');
        }
    };


    // Task drag and drop functions
    const handleTaskDragStart = (e, taskId) => {
        setDraggedTaskId(taskId);
        setIsDragging(true);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target);
        e.stopPropagation(); // Prevent list drag
    };

    const handleTaskDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        e.stopPropagation();
    };

    const handleTaskDrop = async (e, targetTaskId, listId) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (draggedTaskId === null || draggedTaskId === targetTaskId) {
            setDraggedTaskId(null);
            return;
        }

        try {
            setIsReorderingTasks(true);
            
            // Find the list and reorder only active tasks
            const list = lists.find(l => l.id === listId);
            if (!list) return;
            
            const activeTasks = list.tasks.filter(task => !task.completed);
            const completedTasks = list.tasks.filter(task => task.completed);
            
            const draggedIndex = activeTasks.findIndex(task => task.id === draggedTaskId);
            const targetIndex = activeTasks.findIndex(task => task.id === targetTaskId);
            
            // Reorder the active tasks array
            const [draggedTask] = activeTasks.splice(draggedIndex, 1);
            activeTasks.splice(targetIndex, 0, draggedTask);
            
            // Update order positions in the reordered array to be sequential
            const reorderedActiveTasks = activeTasks.map((task, index) => ({
                ...task,
                order_position: index + 1
            }));
            
            // Combine active and completed tasks
            const reorderedTasks = [...reorderedActiveTasks, ...completedTasks];
            
            // Update local state immediately for responsive UI
            setLists(prev => prev.map(list => 
                list.id === listId 
                    ? { ...list, tasks: reorderedTasks }
                    : list
            ));
            
            // Send ALL active tasks with their new positions to the server
            const taskUpdates = reorderedActiveTasks.map((task, index) => ({
                id: task.id,
                order_position: index + 1
            }));
            
            const response = await fetch(`${API_URL}/api/tasks/reorder`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    listId, 
                    taskUpdates,
                    userId: user ? user.id : null
                }),
            });

            if (!response.ok) {
                console.error('Failed to reorder tasks:', response.status, response.statusText);
                // Revert the local change if server update failed
                fetchLists();
                alert('Failed to save task order. Please try again.');
            }
        } catch (error) {
            console.error('Error reordering tasks:', error);
            // Revert the local change if there was an error
            fetchLists();
            alert('Error saving task order. Please try again.');
        } finally {
            setDraggedTaskId(null);
            setIsReorderingTasks(false);
        }
    };

    const handleTaskDragEnd = () => {
        setDraggedTaskId(null);
        setIsDragging(false);
    };

    // Touch event handlers for mobile drag and drop
    const handleTouchStart = (e, taskId) => {
        // Only handle single finger touches
        if (e.touches.length !== 1) return;
        
        setTouchStartTime(Date.now());
        setDraggedTaskId(taskId);
        setIsDragging(false); // Not dragging yet, just touching
        
        // Don't prevent default here - allow normal scrolling
    };

    const handleTouchMove = (e) => {
        if (!draggedTaskId || !touchStartTime) return;
        
        // Only start dragging after 500ms of touch hold to distinguish from scroll
        const holdTime = Date.now() - touchStartTime;
        if (holdTime > 500 && !isDragging) {
            setIsDragging(true);
            // Enable draggable and add visual feedback only when actually dragging
            const dragElement = document.querySelector(`[data-task-id="${draggedTaskId}"]`);
            if (dragElement) {
                dragElement.setAttribute('draggable', 'true');
                dragElement.style.opacity = '0.5';
            }
        }
        
        // Don't call preventDefault in passive event listeners
        // The CSS touch-action property handles preventing scroll during drag
    };

    const handleTouchEnd = (e) => {
        if (draggedTaskId) {
            // Reset visual state
            const dragElement = document.querySelector(`[data-task-id="${draggedTaskId}"]`);
            if (dragElement) {
                dragElement.style.opacity = '1';
                dragElement.removeAttribute('draggable');
            }
        }
        
        setDraggedTaskId(null);
        setIsDragging(false);
        setTouchStartTime(null);
    };

    // Helper function to render a task section
        const renderTaskSection = (tasks, listId, sectionType, sectionTitle, iconClass, list, opts = {}) => {
        const { sectionId = null } = opts;
        const sectionForActions = opts.section || null;
        const selectedCount = selectedTaskIds.size;
        const sectionTaskIds = tasks.map((t) => t.id);
        const sectionSelectedCount = sectionTaskIds.reduce(
            (acc, id) => acc + (selectedTaskIds.has(id) ? 1 : 0),
            0
        );
        const allInSectionSelected = sectionTaskIds.length > 0 && sectionSelectedCount === sectionTaskIds.length;

        const isSectionCollapsed = sectionType === 'completed'
            ? completedSectionCollapsed
            : (sectionId === null ? activeSectionCollapsed : collapsedActiveSectionIds.has(sectionId));

        return (
            <div className="mb-2">
                <div 
                    className="task-section-header d-flex align-items-center gap-2"
                    style={{ userSelect: 'none' }}
                    role="button"
                    tabIndex={0}
                    aria-label={sectionType === 'completed'
                        ? (completedSectionCollapsed ? `Expand ${sectionTitle}` : `Collapse ${sectionTitle}`)
                        : (((sectionId === null ? activeSectionCollapsed : collapsedActiveSectionIds.has(sectionId)))
                            ? `Expand ${sectionTitle}`
                            : `Collapse ${sectionTitle}`)
                    }
                    onClick={(e) => {
                        // Ignore right-clicks (they should trigger context menu instead)
                        if (e.button !== 0) return;
                        
                        const interactive = e.target.closest('button, a, input, select, textarea, label, [data-no-toggle]');
                        if (interactive) return;

                        if (sectionType === 'completed') {
                            setCompletedSectionCollapsed(!completedSectionCollapsed);
                            return;
                        }
                        if (sectionType === 'active') {
                            if (sectionId === null) {
                                setActiveSectionCollapsed(!activeSectionCollapsed);
                            } else {
                                toggleActiveSectionCollapsed(sectionId);
                            }
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.currentTarget.click();
                    }}
                    onContextMenu={(e) => {
                        if (sectionType !== 'active') return;
                        if (!sectionForActions) return;
                        // Prevent editing "Not Sectioned" section
                        if ((sectionForActions.title || '').trim().toLowerCase() === 'not sectioned') return;
                        e.preventDefault();
                        openSectionActionsMenuAtEvent(e, listId, sectionForActions);
                    }}
                    onTouchStart={(e) => {
                        if (sectionType !== 'active') return;
                        if (!sectionForActions) return;
                        // Prevent editing "Not Sectioned" section
                        if ((sectionForActions.title || '').trim().toLowerCase() === 'not sectioned') return;

                        if (sectionLongPressTimerRef.current) {
                            clearTimeout(sectionLongPressTimerRef.current);
                        }
                        const touch = e.touches?.[0];
                        sectionLongPressStartRef.current = {
                            x: touch ? touch.clientX : 0,
                            y: touch ? touch.clientY : 0,
                            didMove: false,
                        };

                        sectionLongPressTimerRef.current = setTimeout(() => {
                            if (sectionLongPressStartRef.current.didMove) return;
                            openSectionActionsMenuAtEvent(e, listId, sectionForActions);
                        }, LONG_PRESS_DELAY);
                    }}
                    onTouchMove={(e) => {
                        if (!sectionLongPressTimerRef.current) return;
                        const touch = e.touches?.[0];
                        if (!touch) return;
                        const dx = Math.abs(touch.clientX - sectionLongPressStartRef.current.x);
                        const dy = Math.abs(touch.clientY - sectionLongPressStartRef.current.y);
                        if (dx > 10 || dy > 10) {
                            sectionLongPressStartRef.current.didMove = true;
                            clearTimeout(sectionLongPressTimerRef.current);
                            sectionLongPressTimerRef.current = null;
                        }
                    }}
                    onTouchEnd={() => {
                        if (sectionLongPressTimerRef.current) {
                            clearTimeout(sectionLongPressTimerRef.current);
                            sectionLongPressTimerRef.current = null;
                        }
                    }}
                >
                    {sectionType !== 'active' && <i className={`bi ${iconClass} me-2`}></i>}
                    <h6 className="mb-0 text-muted flex-grow-1" style={{minWidth: 0}}>{sectionTitle}</h6>
                    <span className="ms-2 text-muted" style={{fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums'}}>{tasks.length}</span>

                    {sectionType === 'active' && (isMultiSelectMode || isDeleteMode) && (
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className={`btn btn-sm ${allInSectionSelected ? (isDeleteMode ? 'btn-danger' : 'btn-primary') : (isDeleteMode ? 'btn-outline-danger' : 'btn-outline-primary')}`}
                                data-no-toggle
                                onClick={() => toggleSelectAllInSection(sectionTaskIds)}
                                disabled={bulkMoveInProgress || sectionTaskIds.length === 0}
                                aria-label={allInSectionSelected ? 'Deselect all tasks in this section' : 'Select all tasks in this section'}
                                title={allInSectionSelected ? 'Deselect all' : 'Select all'}
                                style={{ paddingLeft: '0.375rem', paddingRight: '0.375rem' }}
                            >
                                <i className={`bi ${allInSectionSelected ? 'bi-check2-square' : (sectionSelectedCount > 0 ? 'bi-dash-square' : 'bi-square')}`} aria-hidden="true"></i>
                            </button>

                            <button
                                type="button"
                                className={`btn btn-sm ${isDeleteMode ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                                onClick={() => clearTaskSelection()}
                                disabled={bulkMoveInProgress || selectedCount === 0}
                                aria-label="Clear selection"
                                title="Clear selection"
                            >
                                <i className="bi bi-x-lg" aria-hidden="true"></i>
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary listpage-collapse-btn"
                        onClick={() => {
                            if (sectionType === 'completed') {
                                setCompletedSectionCollapsed(!completedSectionCollapsed);
                                return;
                            }
                            if (sectionType === 'active') {
                                if (sectionId === null) {
                                    setActiveSectionCollapsed(!activeSectionCollapsed);
                                } else {
                                    toggleActiveSectionCollapsed(sectionId);
                                }
                            }
                        }}
                        aria-label={sectionType === 'completed'
                            ? (completedSectionCollapsed ? 'Expand completed section' : 'Collapse completed section')
                            : ((sectionId === null ? activeSectionCollapsed : collapsedActiveSectionIds.has(sectionId))
                                ? 'Expand section'
                                : 'Collapse section')
                        }
                        title={sectionType === 'completed'
                            ? (completedSectionCollapsed ? 'Expand' : 'Collapse')
                            : ((sectionId === null ? activeSectionCollapsed : collapsedActiveSectionIds.has(sectionId))
                                ? 'Expand'
                                : 'Collapse')
                        }
                    >
                        <i
                            className={`bi ${isSectionCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}
                            aria-hidden="true"
                        ></i>
                    </button>
                </div>
                {!isSectionCollapsed && (
                    tasks.length === 0 ? (
                        <div className="text-center py-1 text-muted small">
                            {sectionType === 'active' ? 'No active tasks' : 'No completed tasks'}
                        </div>
                    ) : (
                        <div className="list-group list-group-flush">
                            {tasks.map((task) => (
                        <div 
                            key={task.id} 
                            className={`list-group-item d-flex align-items-center justify-content-between px-2 py-1 border-0 border-bottom ${draggedTaskId === task.id ? 'opacity-50' : ''} ${isReorderingTasks ? 'pointer-events-none' : ''} ${sectionType === 'completed' ? 'bg-light' : ''} ${(list.task_order_type || 'alphabetical_asc') !== 'custom' ? 'non-draggable-task' : ''}`}
                            data-task-id={task.id}
                            draggable={!isReorderingTasks && tasks.length > 1 && sectionType === 'active' && (list.task_order_type || 'alphabetical_asc') === 'custom'}
                            onDragStart={(e) => sectionType === 'active' ? handleTaskDragStart(e, task.id) : null}
                            onDragOver={sectionType === 'active' ? handleTaskDragOver : null}
                            onDrop={(e) => sectionType === 'active' ? handleTaskDrop(e, task.id, listId) : null}
                            onDragEnd={sectionType === 'active' ? handleTaskDragEnd : null}
                            onContextMenu={(e) => {
                                // Desktop right-click can open the same menu
                                if (isDragging || isReorderingTasks) return;
                                e.preventDefault();
                                openTaskActionsMenuAtEvent(e, listId, task);
                            }}
                            onClick={(e) => {
                                // Ignore right-clicks (they should trigger context menu instead)
                                if (e.button !== 0) return;
                                
                                if (isDragging || isReorderingTasks) return;
                                // In multi-select or delete mode, clicking anywhere on the task toggles selection
                                if (isMultiSelectMode || isDeleteMode) {
                                    toggleTaskSelected(task.id);
                                }
                            }}
                            onTouchStart={(e) => {
                                if (isDragging || isReorderingTasks) return;

                                // Double tap detection
                                const now = Date.now();
                                const tapTarget = `task-${task.id}`;
                                const timeSinceLastTap = now - lastTapTimeRef.current;
                                
                                if (timeSinceLastTap < DOUBLE_TAP_DELAY && lastTapTargetRef.current === tapTarget) {
                                    // Double tap detected - open menu with small delay
                                    if (longPressTimerRef.current) {
                                        clearTimeout(longPressTimerRef.current);
                                    }
                                    setTimeout(() => {
                                        openTaskActionsMenuAtEvent(e, listId, task);
                                    }, 200);
                                    lastTapTimeRef.current = 0; // Reset to prevent triple tap
                                    lastTapTargetRef.current = null;
                                    return;
                                }

                                // Record this tap
                                lastTapTimeRef.current = now;
                                lastTapTargetRef.current = tapTarget;

                                // Start long-press timer
                                if (longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current);
                                }

                                const touch = e.touches?.[0];
                                longPressStartRef.current = {
                                    x: touch ? touch.clientX : 0,
                                    y: touch ? touch.clientY : 0,
                                    didMove: false,
                                };

                                longPressTimerRef.current = setTimeout(() => {
                                    // If user moved finger, treat as scroll; abort.
                                    if (longPressStartRef.current.didMove) return;
                                    openTaskActionsMenuAtEvent(e, listId, task);
                                }, LONG_PRESS_DELAY);

                                // Keep existing drag behavior
                                if (sectionType === 'active') {
                                    handleTouchStart(e, task.id);
                                }
                            }}
                            onTouchMove={(e) => {
                                // Cancel long press on meaningful movement to avoid firing during scroll
                                const touch = e.touches?.[0];
                                const start = longPressStartRef.current;
                                if (touch) {
                                    const dx = Math.abs(touch.clientX - (start.x || 0));
                                    const dy = Math.abs(touch.clientY - (start.y || 0));
                                    if (dx > 10 || dy > 10) {
                                        start.didMove = true;
                                        if (longPressTimerRef.current) {
                                            clearTimeout(longPressTimerRef.current);
                                            longPressTimerRef.current = null;
                                        }
                                    }
                                }

                                if (sectionType === 'active') {
                                    handleTouchMove(e);
                                }
                            }}
                            onTouchEnd={(e) => {
                                if (longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current);
                                    longPressTimerRef.current = null;
                                }
                                if (sectionType === 'active') {
                                    handleTouchEnd(e);
                                }
                            }}
                            style={{
                                cursor: (isMultiSelectMode || isDeleteMode) 
                                    ? 'pointer' 
                                    : (tasks.length > 1 && sectionType === 'active' && (list.task_order_type || 'alphabetical_asc') === 'custom') 
                                        ? (draggedTaskId === task.id ? 'grabbing' : 'grab') 
                                        : 'default',
                                transition: 'opacity 0.2s ease',
                                touchAction: (sectionType === 'active' && (list.task_order_type || 'alphabetical_asc') === 'custom') ? 'pan-y' : 'auto',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                WebkitTouchCallout: 'none'
                            }}
                        >
                            {tasks.length > 1 && sectionType === 'active' && (list.task_order_type || 'alphabetical_asc') === 'custom' && (
                                <div className="me-2 text-muted d-flex align-items-center" style={{fontSize: '0.8rem'}}>
                                    <i className="bi bi-grip-vertical"></i>
                                </div>
                            )}
                            <div className="d-flex align-items-center flex-grow-1">
                                {/* Show checkbox for checklist type, or for simple lists in multi-select/delete mode */}
                                {(list.list_type !== 'simple' || isMultiSelectMode || isDeleteMode) && (
                                    <input
                                        className={`form-check-input me-3 mt-2 mb-2 ${
                                            isDeleteMode ? 'delete-mode-checkbox' : 
                                            (task.completed && !isMultiSelectMode && list.list_type !== 'simple') ? 'completed-checkbox' : ''
                                        }`}
                                        type="checkbox"
                                        checked={(isMultiSelectMode || isDeleteMode) ? selectedTaskIds.has(task.id) : task.completed}
                                        onChange={(e) => {
                                            // Stop propagation to prevent double-toggle with task element onClick
                                            e.stopPropagation();
                                            
                                            if (isMultiSelectMode || isDeleteMode) {
                                                toggleTaskSelected(task.id);
                                                return;
                                            }
                                            // Only allow completion toggle for checklist type
                                            if (list.list_type !== 'simple') {
                                                toggleTaskComplete(listId, task.id);
                                            }
                                        }}
                                        onClick={(e) => {
                                            // Stop propagation to prevent task element onClick from firing
                                            e.stopPropagation();
                                        }}
                                        style={{transform: 'scale(1.2)', flexShrink: 0}}
                                    />
                                )}
                                <div className="flex-grow-1" style={{minWidth: 0}}>
                                    <span 
                                        className={`${task.completed && list.list_type !== 'simple' ? 'text-decoration-line-through' : 'text-dark'}`}
                                        style={{
                                            fontSize: '1.1rem',
                                            wordWrap: 'break-word',
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            lineHeight: '1.4',
                                            display: 'inline-block',
                                            width: '100%',
                                            cursor: 'default',
                                            color: (task.completed && list.list_type !== 'simple') ? '#6c757d' : undefined
                                        }}
                                    >
                                        {task.text}
                                    </span>
                                    {task.note && task.note.trim() && (
                                        <div 
                                            className={`small mt-1 ${task.completed && list.list_type !== 'simple' ? 'text-decoration-line-through' : 'text-muted'}`}
                                            style={{
                                                fontSize: '0.85rem',
                                                fontStyle: 'italic',
                                                wordWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '100%',
                                                color: (task.completed && list.list_type !== 'simple') ? 'rgb(108, 117, 125)' : undefined
                                            }}
                                        >
                                            <i className="bi bi-sticky me-1"></i>
                                            {task.note.length > 50 ? `${task.note.substring(0, 50)}...` : task.note}
                                        </div>
                                    )}
                                    {task.due_date && (
                                        <div 
                                            className={`small mt-1 ${task.completed && list.list_type !== 'simple' ? 'text-decoration-line-through' : ''}`}
                                            style={{
                                                fontSize: '0.85rem',
                                                wordWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '100%',
                                                color: (() => {
                                                    if (task.completed && list.list_type !== 'simple') return 'rgb(108, 117, 125)';
                                                    const dueDate = new Date(task.due_date);
                                                    const now = new Date();
                                                    const isOverdue = dueDate < now && !task.completed;
                                                    return isOverdue ? '#dc3545' : '#6c757d';
                                                })()
                                            }}
                                        >
                                            <i className="bi bi-calendar-event me-1"></i>
                                            {(() => {
                                                const dueDate = new Date(task.due_date);
                                                const now = new Date();
                                                const isToday = dueDate.toDateString() === now.toDateString();
                                                const isTomorrow = new Date(now.getTime() + 86400000).toDateString() === dueDate.toDateString();
                                                
                                                let dateStr;
                                                if (isToday) {
                                                    dateStr = 'Today';
                                                } else if (isTomorrow) {
                                                    dateStr = 'Tomorrow';
                                                } else {
                                                    dateStr = dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                                }
                                                
                                                const timeStr = dueDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                                return `${dateStr} at ${timeStr}`;
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                            ))}
                        </div>
                    )
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div 
                className="container-fluid" 
                style={{
                    backgroundColor: 'var(--bs-body-bg, #ffffff)', 
                    minHeight: '100vh', 
                    width: '100vw',
                    padding: 0,
                    margin: 0,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'auto'
                }}
            >
                <nav className="navbar px-3 py-2">
                    <Hamburger />
                </nav>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                        <span className="visually-hidden">Loading lists...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading your task lists...</p>
                </div>
            </div>
        );
    }

    return (
        <div 
            className="container-fluid" 
            style={{
                backgroundColor: 'var(--bs-body-bg, #ffffff)', 
                minHeight: '100vh', 
                width: '100vw',
                padding: 0,
                margin: 0,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto'
            }}
        >
            {/* Long-press task actions menu */}
            {taskActionsMenu.open && taskActionsMenu.task && (
                <div
                    ref={taskActionsMenuRef}
                    className="dropdown-menu show shadow"
                    role="menu"
                    aria-label="Task actions"
                    style={{
                        position: 'fixed',
                        // Calculate position to keep menu within viewport
                        left: (() => {
                            const menuWidth = 260;
                            const x = taskActionsMenu.x;
                            // Keep menu on screen horizontally
                            if (x + menuWidth > window.innerWidth) {
                                return Math.max(8, window.innerWidth - menuWidth - 8);
                            }
                            return Math.max(8, x);
                        })(),
                        top: (() => {
                            const menuRef = taskActionsMenuRef.current;
                            const menuHeight = menuRef ? menuRef.offsetHeight : 400; // Estimate if not rendered yet
                            const y = taskActionsMenu.y;
                            // If menu would go off bottom, position it above the click point or at top of screen
                            if (y + menuHeight > window.innerHeight) {
                                // Try to position above the click point
                                const aboveY = y - menuHeight;
                                if (aboveY < 8) {
                                    // Not enough room above, clamp to bottom of screen
                                    return Math.max(8, window.innerHeight - menuHeight - 8);
                                }
                                return aboveY;
                            }
                            return Math.max(8, y);
                        })(),
                        zIndex: 1200,
                        display: 'block',
                        minWidth: 240,
                        maxHeight: 'calc(100vh - 16px)', // Ensure menu never exceeds viewport
                        overflowY: 'auto', // Allow scrolling if menu is very tall
                        visibility: 'visible',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        WebkitTouchCallout: 'none'
                    }}
                >
                    <div className="px-3 py-2 small text-muted" style={{ maxWidth: 260 }}>
                        <div style={{ fontWeight: 500, marginBottom: taskActionsMenu.task?.note ? '0.5rem' : 0 }}>
                            {taskActionsMenu.task?.text || 'Unnamed task'}
                        </div>
                        {taskActionsMenu.task?.note && taskActionsMenu.task.note.trim() && (
                            <div 
                                className="task-note-display"
                                style={{ 
                                    fontSize: '0.85rem',
                                    fontStyle: 'italic',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    backgroundColor: '#f8f9fa',
                                    padding: '0.5rem',
                                    borderRadius: '4px',
                                    borderLeft: '3px solid #6c757d'
                                }}
                            >
                                <i className="bi bi-sticky me-1"></i>
                                {taskActionsMenu.task.note}
                            </div>
                        )}
                    </div>
                    <div className="dropdown-divider" />

                    {/* Move to section (first action) */}
                    <div className="px-3 pt-2 pb-1 small text-muted">Move to section</div>
                    {(() => {
                        const list = lists.find((l) => l.id === taskActionsMenu.listId);
                        const sections = list?.active_sections || [];

                        // Don't include the current section as a move target.
                        // A task with a null active_section_id is treated as belonging to the first section.
                        const defaultSectionId = sections.length > 0 ? sections[0].id : null;
                        const currentSectionId = (taskActionsMenu.task?.active_section_id ?? null) ?? defaultSectionId;

                        const moveTargets = sections.filter((s) => s.id !== currentSectionId);
                        if (sections.length === 0) {
                            return (
                                <div className="px-3 pb-2 small text-muted">No sections available.</div>
                            );
                        }

                        if (moveTargets.length === 0) {
                            return (
                                <div className="px-3 pb-2 small text-muted">No other sections available.</div>
                            );
                        }

                        return moveTargets.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                className="dropdown-item"
                                onClick={async () => {
                                    await moveTaskToSection(taskActionsMenu.listId, taskActionsMenu.task, s.id);
                                    setTaskActionsMenu((prev) => ({ ...prev, open: false }));
                                }}
                            >
                                {s.title}
                            </button>
                        ));
                    })()}

                    <div className="dropdown-divider" />

                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                            openEditTaskModal(taskActionsMenu.listId, taskActionsMenu.task);
                            setTaskActionsMenu((prev) => ({ ...prev, open: false }));
                        }}
                    >
                        Edit Task…
                    </button>

                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={async () => {
                            await cloneTask(taskActionsMenu.listId, taskActionsMenu.task);
                            setTaskActionsMenu((prev) => ({ ...prev, open: false }));
                        }}
                    >
                        Clone
                    </button>

                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                            openEditNoteModal(taskActionsMenu.listId, taskActionsMenu.task);
                            setTaskActionsMenu((prev) => ({ ...prev, open: false }));
                        }}
                    >
                        {taskActionsMenu.task.note && taskActionsMenu.task.note.trim() ? 'Edit Note…' : 'Add Note…'}
                    </button>

                    <div className="dropdown-divider" />

                    <button
                        type="button"
                        className="dropdown-item text-danger"
                        onClick={async () => {
                            await deleteTask(taskActionsMenu.listId, taskActionsMenu.task.id);
                            setTaskActionsMenu((prev) => ({ ...prev, open: false }));
                        }}
                    >
                        Delete
                    </button>

                    <div className="dropdown-divider" />
                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => setTaskActionsMenu((prev) => ({ ...prev, open: false }))}
                    >
                        Close
                    </button>
                </div>
            )}

            {/* Long-press section actions menu */}
            {sectionActionsMenu.open && sectionActionsMenu.section && (
                <div
                    ref={sectionActionsMenuRef}
                    className="dropdown-menu show shadow"
                    role="menu"
                    aria-label="Section actions"
                    style={{
                        position: 'fixed',
                        // Calculate position to keep menu within viewport
                        left: (() => {
                            const menuWidth = 260;
                            const x = sectionActionsMenu.x;
                            // Keep menu on screen horizontally
                            if (x + menuWidth > window.innerWidth) {
                                return Math.max(8, window.innerWidth - menuWidth - 8);
                            }
                            return Math.max(8, x);
                        })(),
                        top: (() => {
                            const menuRef = sectionActionsMenuRef.current;
                            const menuHeight = menuRef ? menuRef.offsetHeight : 300; // Estimate if not rendered yet
                            const y = sectionActionsMenu.y;
                            // If menu would go off bottom, position it above the click point or at top of screen
                            if (y + menuHeight > window.innerHeight) {
                                // Try to position above the click point
                                const aboveY = y - menuHeight;
                                if (aboveY < 8) {
                                    // Not enough room above, clamp to bottom of screen
                                    return Math.max(8, window.innerHeight - menuHeight - 8);
                                }
                                return aboveY;
                            }
                            return Math.max(8, y);
                        })(),
                        zIndex: 1200,
                        display: 'block',
                        minWidth: 240,
                        maxHeight: 'calc(100vh - 16px)', // Ensure menu never exceeds viewport
                        overflowY: 'auto', // Allow scrolling if menu is very tall
                        visibility: 'visible',
                    }}
                >
                    <div className="px-3 py-2 small text-muted" style={{ maxWidth: 260 }}>
                        {sectionActionsMenu.section?.title || 'Unnamed section'}
                    </div>
                    <div className="dropdown-divider" />

                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={async () => {
                            await renameActiveSection(sectionActionsMenu.section);
                            setSectionActionsMenu((prev) => ({ ...prev, open: false }));
                        }}
                    >
                        Rename…
                    </button>

                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                            toggleActiveSectionCollapsed(sectionActionsMenu.section.id);
                            setSectionActionsMenu((prev) => ({ ...prev, open: false }));
                        }}
                    >
                        {collapsedActiveSectionIds.has(sectionActionsMenu.section.id) ? 'Expand' : 'Collapse'}
                    </button>

                    <div className="dropdown-divider" />

                    <button
                        type="button"
                        className="dropdown-item text-danger"
                        disabled={(() => {
                            const list = lists.find((l) => l.id === sectionActionsMenu.listId);
                            const sections = list?.active_sections || [];
                            return sections.length <= 1;
                        })()}
                        onClick={async () => {
                            await deleteActiveSection(sectionActionsMenu.section);
                            setSectionActionsMenu((prev) => ({ ...prev, open: false }));
                        }}
                    >
                        Delete…
                    </button>

                    <div className="dropdown-divider" />
                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => setSectionActionsMenu((prev) => ({ ...prev, open: false }))}
                    >
                        Close
                    </button>
                </div>
            )}

            {/* Sticky List Header - Single List View */}
            <div className="sticky-top bg-white border-bottom shadow-sm">
                    <div className="container-fluid py-2">
                        {/* First Row: Hamburger, Create Button, and Dropdown */}
                        <div className="row align-items-center mb-2 mb-md-2">
                            {/* Hamburger Menu - Far Left */}
                            <div className="col-auto pe-0">
                                <Hamburger />
                            </div>
                            
                            {/* Dropdown & Create Button */}
                            <div className="col ps-0">
                                <div className="d-flex align-items-center gap-2">
                                    <button 
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => openCreateListModal()}
                                        title="Create New List"
                                        disabled={isMultiSelectMode || isDeleteMode}
                                    >
                                        <i className="bi bi-plus-lg"></i>
                                    </button>
                                    
                                    {/* List Selector Dropdown */}
                                    <div className="dropdown flex-grow-1">
                                        <button 
                                            className="btn btn-outline-primary w-100 text-start d-flex justify-content-between align-items-center" 
                                            type="button" 
                                            onClick={() => setDropdownOpen(!dropdownOpen)}
                                            aria-expanded={dropdownOpen}
                                            disabled={isMultiSelectMode || isDeleteMode}
                                        >
                                            <span>
                                                {getSelectedList() ? (
                                                    <>
                                                        <i className="bi bi-list-ul me-2"></i>
                                                        {getSelectedList().name}
                                                    </>
                                                ) : (
                                                    <span className="text-muted">Select a list...</span>
                                                )}
                                            </span>
                                            <i className={`bi bi-chevron-${dropdownOpen ? 'up' : 'down'}`}></i>
                                        </button>
                                        {dropdownOpen && (
                                            <ul className="dropdown-menu show w-100" style={{ position: 'absolute', top: '100%', left: '0', display: 'block', transform: 'none', zIndex: 1000 }}>
                                                {lists.map(list => (
                                                    <li key={list.id}>
                                                        <button 
                                                            className={`dropdown-item d-flex justify-content-between align-items-center ${list.id === selectedListId ? 'active' : ''}`}
                                                            onClick={() => handleListSelection(list.id)}
                                                        >
                                                            <span>
                                                                <i className="bi bi-list-ul me-2"></i>
                                                                {list.name}
                                                            </span>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    {/* 3-dots actions menu (to the right of the list dropdown) */}
                                    <div className="dropdown" ref={actionsMenuRef}>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            aria-expanded={actionsMenuOpen}
                                            aria-haspopup="menu"
                                            title="List actions"
                                            onClick={() => setActionsMenuOpen((v) => !v)}
                                            disabled={isMultiSelectMode || isDeleteMode}
                                        >
                                            <i className="bi bi-three-dots-vertical"></i>
                                        </button>

                                        <ul
                                            className={`dropdown-menu dropdown-menu-end shadow ${actionsMenuOpen ? 'show' : ''}`}
                                            style={{
                                                display: actionsMenuOpen ? 'block' : 'none',
                                                position: 'absolute',
                                                top: '100%',
                                                right: 0,
                                                zIndex: 1100,
                                            }}
                                            role="menu"
                                        >
                                            {/* List Description (if present) */}
                                            {getSelectedList()?.description?.trim() && (
                                                <>
                                                    <li className="px-3 py-2">
                                                        <div className="small text-muted mb-1 fw-semibold">
                                                            <i className="bi bi-info-circle me-1"></i>
                                                            Description
                                                        </div>
                                                        <div className="small text-dark" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                            {getSelectedList().description}
                                                        </div>
                                                    </li>
                                                    <li><hr className="dropdown-divider" /></li>
                                                </>
                                            )}

                                            {/* Edit List (toggle) */}
                                            <li>
                                                <button
                                                    type="button"
                                                    className="dropdown-item"
                                                    disabled={!selectedListId}
                                                    onClick={() => {
                                                        const selectedList = getSelectedList();
                                                        if (!selectedList) return;

                                                        // Toggle edit mode - if already editing this list, cancel
                                                        if (editingList && editingList.id === selectedList.id) {
                                                            setEditingList(null);
                                                            setNewListName('');
                                                            setNewListDescription('');
                                                            setEditingActiveHeader('');
                                                            setEditingCompletedHeader('');
                                                            setEditingTaskOrderType('custom');
                                                            setEditingListType('checklist');
                                                            setShowCreateForm(false);
                                                        } else {
                                                            handleEditList(selectedList);
                                                        }

                                                        setActionsMenuOpen(false);
                                                    }}
                                                >
                                                    <i className={`bi ${editingList ? 'bi-x-circle' : 'bi-pencil'} me-2`}></i>
                                                    {editingList ? 'Cancel Edit List' : 'Edit List'}
                                                </button>
                                            </li>

                                            <li><hr className="dropdown-divider" /></li>

                                            {/* Active Sections */}
                                            {(() => {
                                                const selectedList = getSelectedList();
                                                const canEditSections = !!(selectedListId && selectedList && !selectedList.locked);
                                                

                                                return (
                                                    <>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="dropdown-item"
                                                                disabled={!canEditSections}
                                                                onClick={() => {
                                                                    createActiveSection(selectedListId);
                                                                    setActionsMenuOpen(false);
                                                                }}
                                                            >
                                                                <i className="bi bi-folder-plus me-2"></i>
                                                                Add Section…
                                                            </button>
                                                        </li>

                                                        <li><hr className="dropdown-divider" /></li>
                                                    </>
                                                );
                                            })()}

                                            {/* Lock/Unlock */}
                                            {selectedListId && (() => {
                                                const selectedList = getSelectedList();
                                                if (!selectedList) return null;

                                                return (
                                                    <li>
                                                        <button
                                                            type="button"
                                                            className="dropdown-item"
                                                            onClick={() => {
                                                                if (lockingListId !== selectedList.id) {
                                                                    handleToggleLock(selectedList.id, selectedList.locked);
                                                                }

                                                                setActionsMenuOpen(false);
                                                            }}
                                                            disabled={lockingListId === selectedList.id}
                                                        >
                                                            {lockingListId === selectedList.id ? (
                                                                <>
                                                                    <i className="bi bi-arrow-clockwise me-2" style={{animation: 'spin 1s linear infinite'}}></i>
                                                                    Processing…
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className={`bi bi-${selectedList.locked ? 'lock' : 'unlock'} me-2`}></i>
                                                                    {selectedList.locked ? 'Unlock List' : 'Lock List'}
                                                                </>
                                                            )}
                                                        </button>
                                                    </li>
                                                );
                                            })()}

                                            {/* Add User to List - Only show for list owners */}
                                            {user && selectedListId && (() => {
                                                const selectedList = getSelectedList();
                                                if (!selectedList) return null;
                                                // Only show if user is the owner
                                                if (selectedList.owner_id !== user.id) return null;

                                                return (
                                                    <>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    openAddUserModal(selectedList.id);
                                                                    setActionsMenuOpen(false);
                                                                }}
                                                            >
                                                                <i className="bi bi-person-plus me-2"></i>
                                                                Add User to List
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    openManageUsersModal(selectedList.id);
                                                                    setActionsMenuOpen(false);
                                                                }}
                                                            >
                                                                <i className="bi bi-people me-2"></i>
                                                                Manage Users
                                                            </button>
                                                        </li>
                                                    </>
                                                );
                                            })()}

                                            {/* Reset sections - move all tasks to Not Sectioned */}
                                            {selectedListId && (() => {
                                                const selectedList = getSelectedList();
                                                const hasActiveSections = selectedList?.active_sections?.length > 0;
                                                const hasTasks = selectedList?.tasks?.length > 0;
                                                const canReset = !!(selectedList && !selectedList.locked && hasActiveSections && hasTasks);
                                                if (!selectedList) return null;

                                                return (
                                                    <li>
                                                        <button
                                                            type="button"
                                                            className="dropdown-item"
                                                            disabled={!canReset}
                                                            onClick={() => {
                                                                if (canReset) handleResetSections(selectedList.id);
                                                                setActionsMenuOpen(false);
                                                            }}
                                                        >
                                                            <i className="bi bi-arrow-counterclockwise me-2"></i>
                                                            Reset Sections
                                                        </button>
                                                    </li>
                                                );
                                            })()}

                                            {/* Clear all tasks */}
                                            {selectedListId && (() => {
                                                const selectedList = getSelectedList();
                                                const canClear = !!(selectedList && !selectedList.locked && selectedList.tasks && selectedList.tasks.length > 0);
                                                if (!selectedList) return null;

                                                return (
                                                    <li>
                                                        <button
                                                            type="button"
                                                            className="dropdown-item text-warning"
                                                            disabled={!canClear}
                                                            onClick={() => {
                                                                if (canClear) handleClearList(selectedList.id);
                                                                setActionsMenuOpen(false);
                                                            }}
                                                        >
                                                            <i className="bi bi-eraser me-2"></i>
                                                            Clear All Tasks
                                                        </button>
                                                    </li>
                                                );
                                            })()}

                                            {/* Delete list */}
                                            {selectedListId && (() => {
                                                const selectedList = getSelectedList();
                                                const canDelete = !!(selectedList && !selectedList.locked);
                                                if (!selectedList) return null;

                                                return (
                                                    <li>
                                                        <button
                                                            type="button"
                                                            className="dropdown-item text-danger"
                                                            disabled={!canDelete}
                                                            onClick={() => {
                                                                if (canDelete) handleDeleteList(selectedList.id);
                                                                setActionsMenuOpen(false);
                                                            }}
                                                        >
                                                            <i className="bi bi-trash me-2"></i>
                                                            Delete List
                                                        </button>
                                                    </li>
                                                );
                                            })()}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Second Row: Action Buttons (below dropdown on desktop, stacked on mobile) */}
                        <div className="row">
                            <div className="col">
                                <div className="d-flex justify-content-start">
                                    <button 
                                        className="btn btn-outline-success btn-sm"
                                        onClick={() => {
                                            if (!selectedListId) return;
                                            openAddTaskModal();
                                        }}
                                        disabled={!selectedListId || isMultiSelectMode || isDeleteMode}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i>
                                        Add
                                    </button>

                                    <button
                                        type="button"
                                        className={`btn btn-sm ms-2 ${isMultiSelectMode ? 'btn-primary' : 'btn-outline-primary'} btn-move-toggle`}
                                        onClick={() => {
                                            if (!selectedListId) return;
                                            setIsMultiSelectMode((prev) => {
                                                const next = !prev;
                                                if (!next) {
                                                    clearTaskSelection();
                                                }
                                                return next;
                                            });
                                        }}
                                        disabled={!selectedListId || bulkMoveInProgress || isDeleteMode}
                                        aria-pressed={isMultiSelectMode}
                                        aria-label={isMultiSelectMode ? 'Exit multi-select mode' : 'Enter multi-select mode'}
                                        title={isMultiSelectMode ? 'Exit multi-select' : 'Multi-select'}
                                    >
                                        {/* Mobile: show move icon. Desktop: show select-mode checkbox icon. */}
                                        <span className="d-inline d-sm-none">
                                            <i className="bi bi-arrows-move" aria-hidden="true"></i>
                                        </span>
                                        <span className="d-none d-sm-inline">
                                            <i className={`bi ${isMultiSelectMode ? 'bi-check2-square' : 'bi-square'}`} aria-hidden="true"></i>
                                        </span>
                                        <span className="ms-1 d-none d-sm-inline">Select</span>
                                    </button>

                                    {isMultiSelectMode && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary btn-move-toggle ms-2"
                                            onClick={(e) => {
                                                if (!selectedListId) return;
                                                if (selectedTaskIds.size === 0) return;
                                                const list = getSelectedList();
                                                if (!list?.active_sections || list.active_sections.length === 0) return;
                                                if (bulkMoveInProgress) return;

                                                // Position menu left of click to avoid off-screen
                                                const touch = e?.touches?.[0] || e?.changedTouches?.[0];
                                                const clientX = touch ? touch.clientX : (e?.clientX ?? 0);
                                                const clientY = touch ? touch.clientY : (e?.clientY ?? 0);
                                                const menuWidth = 240;
                                                const padding = 16;

                                                setBulkMoveMenu({
                                                    open: true,
                                                    x: Math.max(padding, clientX - menuWidth),
                                                    y: clientY + 8,
                                                    listId: selectedListId,
                                                });
                                            }}
                                            disabled={!selectedListId || bulkMoveInProgress || selectedTaskIds.size === 0}
                                            aria-label="Move selected tasks"
                                            title={selectedTaskIds.size === 0 ? 'Select tasks to enable' : 'Move selected tasks'}
                                        >
                                            <i className="bi bi-arrows-move" aria-hidden="true"></i>
                                            <span className="ms-1 d-inline d-sm-none">
                                                {selectedTaskIds.size}
                                            </span>
                                            <span className="ms-1 d-none d-sm-inline">
                                                Move {selectedTaskIds.size} {selectedTaskIds.size === 1 ? 'task' : 'tasks'}
                                            </span>
                                            <i className="bi bi-caret-down-fill ms-1" aria-hidden="true"></i>
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className={`btn btn-sm ms-2 ${isDeleteMode ? 'btn-danger' : 'btn-outline-danger'}`}
                                        onClick={() => {
                                            if (!selectedListId) return;
                                            setIsDeleteMode((prev) => {
                                                const next = !prev;
                                                if (!next) {
                                                    clearTaskSelection();
                                                }
                                                return next;
                                            });
                                        }}
                                        disabled={!selectedListId || isMultiSelectMode}
                                        aria-pressed={isDeleteMode}
                                        title={isDeleteMode ? 'Exit delete mode' : 'Delete mode'}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>

                                    {isDeleteMode && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger ms-2"
                                            onClick={(e) => {
                                                if (!selectedListId) return;
                                                if (selectedTaskIds.size === 0) return;

                                                // Position menu left of click to avoid off-screen
                                                const touch = e?.touches?.[0] || e?.changedTouches?.[0];
                                                const clientX = touch ? touch.clientX : (e?.clientX ?? 0);
                                                const clientY = touch ? touch.clientY : (e?.clientY ?? 0);
                                                const menuWidth = 240;
                                                const padding = 16;

                                                setBulkDeleteMenu({
                                                    open: true,
                                                    x: Math.max(padding, clientX - menuWidth),
                                                    y: clientY + 8,
                                                    listId: selectedListId,
                                                });
                                            }}
                                            disabled={!selectedListId || selectedTaskIds.size === 0}
                                            aria-label="Delete selected tasks"
                                            title={selectedTaskIds.size === 0 ? 'Select tasks to enable' : 'Delete selected tasks'}
                                        >
                                            <i className="bi bi-trash" aria-hidden="true"></i>
                                            <span className="ms-1 d-inline d-sm-none">
                                                {selectedTaskIds.size}
                                            </span>
                                            <span className="ms-1 d-none d-sm-inline">
                                                Delete {selectedTaskIds.size} {selectedTaskIds.size === 1 ? 'task' : 'tasks'}
                                            </span>
                                            <i className="bi bi-caret-down-fill ms-1" aria-hidden="true"></i>
                                        </button>
                                    )}

                                    <button 
                                        className="btn btn-outline-success btn-sm ms-auto"
                                        onClick={() => {
                                            if (!selectedListId) return;
                                            openBulkAddModal();
                                        }}
                                        disabled={!selectedListId || isMultiSelectMode || isDeleteMode}
                                        title="Bulk add tasks"
                                    >
                                        <i className="bi bi-plus-circle me-1"></i>
                                        Bulk
                                    </button>

                                    {/* Bulk delete dropdown (React-controlled) */}
                                    {bulkDeleteMenu.open && bulkDeleteMenu.listId === selectedListId && (
                                        <div
                                            ref={bulkDeleteMenuRef}
                                            className="dropdown-menu show shadow"
                                            style={{
                                                position: 'fixed',
                                                left: bulkDeleteMenu.x,
                                                top: bulkDeleteMenu.y,
                                                zIndex: 2050,
                                                display: 'block',
                                                minWidth: 240,
                                                maxWidth: 280,
                                            }}
                                        >
                                            <h6 className="dropdown-header">Confirm deletion</h6>
                                            <div className="px-3 py-2 text-muted" style={{ fontSize: '0.875rem' }}>
                                                Delete {selectedTaskIds.size} {selectedTaskIds.size === 1 ? 'task' : 'tasks'}?
                                            </div>
                                            <div className="dropdown-divider"></div>
                                            <button
                                                type="button"
                                                className="dropdown-item text-danger"
                                                onClick={() => {
                                                    setBulkDeleteMenu((prev) => ({ ...prev, open: false }));
                                                    handleBulkDeleteSelectedTasks({
                                                        listId: selectedListId,
                                                        taskIds: Array.from(selectedTaskIds)
                                                    });
                                                }}
                                            >
                                                <i className="bi bi-trash me-2"></i>
                                                Confirm Delete
                                            </button>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setBulkDeleteMenu((prev) => ({ ...prev, open: false }));
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}

                                    {/* Bulk move dropdown (React-controlled) */}
                                    {bulkMoveMenu.open && bulkMoveMenu.listId === selectedListId && (
                                        <div
                                            ref={bulkMoveMenuRef}
                                            className="dropdown-menu show shadow"
                                            style={{
                                                position: 'fixed',
                                                left: bulkMoveMenu.x,
                                                top: bulkMoveMenu.y,
                                                zIndex: 2050,
                                                display: 'block',
                                                minWidth: 240,
                                                maxWidth: 280,
                                            }}
                                        >
                                            <h6 className="dropdown-header">Move to section</h6>
                                            {(getSelectedList()?.active_sections || []).map((target) => (
                                                <button
                                                    key={target.id}
                                                    type="button"
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setBulkMoveMenu((prev) => ({ ...prev, open: false }));
                                                        handleBulkMoveSelectedTasks({
                                                            listId: selectedListId,
                                                            taskIds: Array.from(selectedTaskIds),
                                                            targetActiveSectionId: target.id
                                                        });
                                                    }}
                                                >
                                                    {target.title}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            
            {/* Main Content Area */}
            <div className="px-2 px-md-3">
            
            {/* Edit Form */}
            {editingList && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card shadow-sm border-warning">
                            <div className="card-header bg-warning text-dark py-2">
                                <h6 className="mb-0">
                                    <i className="bi bi-pencil me-2"></i>
                                    Edit List: {editingList.name}
                                </h6>
                            </div>
                            <div className="card-body pt-3 px-3 pb-0">
                                <form onSubmit={handleUpdateList}>
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb-2">
                                            <label htmlFor="editListName" className="form-label fw-semibold">
                                                List Name *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-cursor-input"
                                                id="editListName"
                                                value={newListName || ''}
                                                onChange={(e) => setNewListName(e.target.value)}
                                                placeholder="Enter list name..."
                                                required
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-2">
                                            <label htmlFor="editListDescription" className="form-label fw-semibold">
                                                Description
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-cursor-input"
                                                id="editListDescription"
                                                value={newListDescription || ''}
                                                onChange={(e) => setNewListDescription(e.target.value)}
                                                placeholder="Brief description (optional)..."
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-2">
                                            <label htmlFor="editActiveHeader" className="form-label fw-semibold">
                                                Active Tasks Header
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-cursor-input"
                                                id="editActiveHeader"
                                                value={editingActiveHeader}
                                                onChange={(e) => setEditingActiveHeader(e.target.value)}
                                                placeholder="e.g., 'To Do', 'Active Tasks'"
                                            />
                                        </div>
                                        {/* Only show completed header for checklist type */}
                                        {editingListType !== 'simple' && (
                                            <div className="col-12 col-md-6 mb-2">
                                                <label htmlFor="editCompletedHeader" className="form-label fw-semibold">
                                                    Completed Tasks Header
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm text-cursor-input"
                                                    id="editCompletedHeader"
                                                    value={editingCompletedHeader}
                                                    onChange={(e) => setEditingCompletedHeader(e.target.value)}
                                                    placeholder="e.g., 'Done', 'Completed Tasks'"
                                                />
                                            </div>
                                        )}
                                        
                                        {/* Task Ordering Selection */}
                                        {user && (
                                            <div className="col-12 mb-2">
                                                <label htmlFor="editTaskOrderType" className="form-label fw-semibold">
                                                    <i className="bi bi-sort-down me-2"></i>
                                                    Task Ordering
                                                </label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    id="editTaskOrderType"
                                                    value={editingTaskOrderType}
                                                    onChange={(e) => setEditingTaskOrderType(e.target.value)}
                                                >
                                                    <option value="custom">Custom (Drag & Drop)</option>
                                                    <option value="alphabetical_asc">Alphabetical A-Z</option>
                                                    <option value="alphabetical_desc">Alphabetical Z-A</option>
                                                    <option value="date_asc">Date (Oldest First)</option>
                                                    <option value="date_desc">Date (Newest First)</option>
                                                </select>
                                                <div className="form-text small">
                                                    Choose how tasks are ordered in this list
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* List Type Selection */}
                                        <div className="col-12 mb-2">
                                            <label htmlFor="editListType" className="form-label fw-semibold">
                                                <i className="bi bi-list-ul me-2"></i>
                                                List Type
                                            </label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="editListType"
                                                value={editingListType}
                                                onChange={(e) => setEditingListType(e.target.value)}
                                            >
                                                <option value="checklist">Checklist - Items can be checked off</option>
                                                <option value="simple">Simple List - Just a list of items</option>
                                            </select>
                                            {editingListType !== editingList.list_type && (
                                                <div className="alert alert-warning alert-sm mt-2 mb-0 py-1 px-2" style={{fontSize: '0.85rem'}}>
                                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                                    <strong>Note:</strong> Changing list type will delete all sections (except "Not Sectioned"), move all items to "Not Sectioned"
                                                    {editingListType === 'simple' && editingList.tasks?.some(t => t.completed) && 
                                                        ', and uncomplete all tasks'}
                                                    .
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="d-flex gap-2 mt-3">
                                        <button type="submit" className="btn btn-warning btn-sm">
                                            <i className="bi bi-check-circle me-1"></i>
                                            Update List
                                        </button>
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => {
                                                setEditingList(null);
                                                setNewListName('');
                                                setNewListDescription('');
                                                setEditingActiveHeader('');
                                                setEditingCompletedHeader('');
                                                setEditingTaskOrderType('custom');
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Form - Only for new lists */}
            {showCreateForm && !editingList && (
                <div className="row mb-2">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-primary text-white py-2">
                                <h6 className="mb-0">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Create New List
                                </h6>
                            </div>
                            <div className="card-body pt-3 px-3 pb-0">
                                <form onSubmit={handleCreateList}>
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb-2">
                                            <label htmlFor="listName" className="form-label fw-semibold">
                                                List Name *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-cursor-input"
                                                id="listName"
                                                value={newListName || ''}
                                                onChange={(e) => setNewListName(e.target.value)}
                                                placeholder="Enter list name..."
                                                style={{
                                                    cursor: 'text'
                                                }}
                                                required
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-2">
                                            <label htmlFor="listDescription" className="form-label fw-semibold">
                                                Description
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-cursor-input"
                                                id="listDescription"
                                                value={newListDescription || ''}
                                                onChange={(e) => setNewListDescription(e.target.value)}
                                                placeholder="Brief description (optional)..."
                                                style={{
                                                    cursor: 'text'
                                                }}
                                            />
                                        </div>
                                        {/* Subheader fields for both create and edit */}
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="activeHeader" className="form-label fw-semibold">
                                                {newListType === 'simple' ? 'Section Title' : 'Active Section Title'}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control text-cursor-input"
                                                id="activeHeader"
                                                value={editingActiveHeader || ''}
                                                onChange={(e) => setEditingActiveHeader(e.target.value)}
                                                placeholder={newListType === 'simple' ? 'Items' : 'Active Tasks'}
                                                style={{
                                                    cursor: 'text'
                                                }}
                                            />
                                        </div>
                                        {/* Only show completed header for checklist type */}
                                        {newListType !== 'simple' && (
                                            <div className="col-12 col-md-6 mb-3">
                                                <label htmlFor="completedHeader" className="form-label fw-semibold">
                                                    Completed Section Title
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control text-cursor-input"
                                                    id="completedHeader"
                                                    value={editingCompletedHeader || ''}
                                                    onChange={(e) => setEditingCompletedHeader(e.target.value)}
                                                    placeholder="Completed Tasks"
                                                    style={{
                                                        cursor: 'text'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* List Type Selection */}
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <label htmlFor="listType" className="form-label fw-semibold">
                                                <i className="bi bi-list-ul me-2"></i>
                                                List Type *
                                            </label>
                                            <select
                                                className="form-select"
                                                id="listType"
                                                value={newListType}
                                                onChange={(e) => setNewListType(e.target.value)}
                                                required
                                            >
                                                <option value="checklist">Checklist - Items can be checked off</option>
                                                <option value="simple">Simple List - Just a list of items</option>
                                            </select>
                                            <div className="form-text">
                                                Choose whether items can be checked off or are just plain text
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Task Ordering Selection */}
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <label htmlFor="taskOrdering" className="form-label fw-semibold">
                                                <i className="bi bi-sort-down me-2"></i>
                                                Default Task Ordering
                                            </label>
                                            <select
                                                className="form-select"
                                                id="taskOrdering"
                                                value={newListTaskOrder}
                                                onChange={(e) => setNewListTaskOrder(e.target.value)}
                                            >
                                                <option value="custom">Custom (Drag & Drop)</option>
                                                <option value="alphabetical_asc">Alphabetical A-Z</option>
                                                <option value="alphabetical_desc">Alphabetical Z-A</option>
                                                <option value="date_asc">Date (Oldest First)</option>
                                                <option value="date_desc">Date (Newest First)</option>
                                            </select>
                                            <div className="form-text">
                                                Choose the default task ordering (users can customize their own view)
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="d-flex flex-column flex-sm-row gap-2 pb-2">
                                        <button 
                                            type="submit" 
                                            className="btn btn-success"
                                            disabled={!newListName?.trim()}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Create List
                                        </button>
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setShowCreateForm(false);
                                                setEditingList(null);
                                                setNewListName('');
                                                setNewListDescription('');
                                                setNewListTaskOrder('alphabetical_asc');
                                                setNewListType('checklist'); // Reset list type
                                                setEditingActiveHeader('');
                                                setEditingCompletedHeader('');
                                            }}
                                        >
                                            <i className="bi bi-x-circle me-2"></i>
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Lists */}
            {lists.length > 0 ? (
                <div className="row">
                    <div className="col-12">
                        {/* Single List View - Display only the selected list */}
                        {(() => {
                            const selectedList = getSelectedList();
                            if (!selectedList) {
                                return (
                                    <div className="text-center py-5">
                                        <h4 className="text-muted">No list selected</h4>
                                        <p className="text-muted">Please select a list from the dropdown above.</p>
                                    </div>
                                );
                            }
                            
                            return (
                                <div className="card shadow-sm border-0">
                                    <div className="card-body pt-3 px-3 pb-0">
                                        {/* Single List Content */}
                                        
                                        {/* Task Display for Single List */}
                                        <div>
                                            {selectedList.tasks && selectedList.tasks.length > 0 ? (
                                                <div>
                                                    {(() => {
                                                        const activeTasks = selectedList.tasks.filter(task => !task.completed);
                                                        const completedTasks = selectedList.tasks.filter(task => task.completed);

                                                        const activeSections = (selectedList.active_sections || []);
                                                        const defaultSectionId = activeSections.length > 0 ? activeSections[0].id : null;

                                                        // Avoid UI churn: keep a "Not Sectioned" section around for reassignment,
                                                        // but hide it if it's empty so it doesn't clutter the UI.
                                                        const visibleActiveSections = activeSections
                                                            .filter((section) => {
                                                            const title = (section.title || '').trim().toLowerCase();
                                                            if (title !== 'not sectioned') return true;

                                                            const sectionHasTasks = activeTasks.some((t) => {
                                                                const sid = (t.active_section_id ?? null);
                                                                const effectiveSid = sid ?? defaultSectionId;
                                                                return effectiveSid === section.id;
                                                            });

                                                            return sectionHasTasks;
                                                        });
                                                        
                                                        return (
                                                            <>
                                                                {/* Active Sections */}
                                                                {visibleActiveSections.length > 0 ? (
                                                                    visibleActiveSections.map((section) => {
                                                                        const sectionTasks = activeTasks.filter((t) => {
                                                                            const sid = (t.active_section_id ?? null);
                                                                            const effectiveSid = sid ?? defaultSectionId;
                                                                            return effectiveSid === section.id;
                                                                        });

                                                                        return (
                                                                            <div key={section.id}>
                                                                                {renderTaskSection(
                                                                                    sectionTasks,
                                                                                    selectedList.id,
                                                                                    'active',
                                                                                    section.title,
                                                                                    'bi-circle',
                                                                                    selectedList,
                                                                                    { sectionId: section.id, section }
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    // Backward-compatible fallback if active sections don't exist yet
                                                                    renderTaskSection(
                                                                        activeTasks,
                                                                        selectedList.id,
                                                                        'active',
                                                                        selectedList.active_header || 'Active Tasks',
                                                                        'bi-circle',
                                                                        selectedList
                                                                    )
                                                                )}
                                                                
                                                                {/* Only show completed section for checklist type */}
                                                                {selectedList.list_type !== 'simple' && (
                                                                    <>
                                                                        {/* Divider between active sections and completed section */}
                                                                        <hr className="my-2 border-light" style={{opacity: 0.5}} />
                                                                        
                                                                        {/* Completed Tasks */}
                                                                        {renderTaskSection(
                                                                            completedTasks, 
                                                                            selectedList.id, 
                                                                            'completed', 
                                                                            selectedList.completed_header || 'Completed Tasks', 
                                                                            'bi-check-circle-fill', 
                                                                            selectedList,
                                                                            { sectionId: 'completed', section: { id: 'completed', title: selectedList.completed_header || 'Completed Tasks' } }
                                                                        )}
                                                                    </>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4 text-muted">
                                                    <i className="bi bi-list-ul fs-1 d-block mb-2 opacity-50"></i>
                                                    <p className="mb-0">No tasks in this list yet.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            ) : (
                /* Empty State */
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <i className="bi bi-list-check display-1 text-muted mb-3"></i>
                            <h3 className="text-muted mb-3">No Task Lists Yet</h3>
                            <p className="text-muted mb-4">
                                Create your first task list to get organized and stay productive.
                            </p>
                            <button 
                                className="btn btn-primary btn-lg"
                                onClick={() => setShowCreateForm(true)}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Create Your First List
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            </div> {/* End Main Content Area */}

            {/* Add Task Modal */}
            {showAddTaskModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="addTaskModalTitle"
                        onMouseDown={(e) => {
                            // Click outside closes
                            if (e.target === e.currentTarget) closeAddTaskModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        if (!selectedListId) return;
                                        await addTaskToList(selectedListId, newTaskText, newTaskNote);
                                        closeAddTaskModal();
                                    }}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="addTaskModalTitle">
                                            Add Item
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={closeAddTaskModal}
                                        ></button>
                                    </div>

                                    <div className="modal-body">
                                        <div className="mb-3">
                                            <label htmlFor="newTaskInput" className="form-label">
                                                {(() => {
                                                    const selectedList = lists.find(l => l.id === selectedListId);
                                                    return selectedList?.list_type === 'simple' ? 'Item name' : 'Task name';
                                                })()}
                                            </label>
                                            <input
                                                id="newTaskInput"
                                                ref={addTaskInputRef}
                                                type="text"
                                                className="form-control"
                                                value={newTaskText}
                                                onChange={(e) => setNewTaskText(e.target.value)}
                                                placeholder={(() => {
                                                    const selectedList = lists.find(l => l.id === selectedListId);
                                                    return selectedList?.list_type === 'simple' ? 'New item…' : 'New task…';
                                                })()}
                                                autoCapitalize="sentences"
                                                autoCorrect="on"
                                                inputMode="text"
                                                enterKeyHint="done"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="newTaskNote" className="form-label">
                                                Note <span className="text-muted small">(optional)</span>
                                            </label>
                                            <textarea
                                                id="newTaskNote"
                                                className="form-control"
                                                value={newTaskNote}
                                                onChange={(e) => setNewTaskNote(e.target.value)}
                                                placeholder="Add a note…"
                                                rows="3"
                                                autoCapitalize="sentences"
                                                autoCorrect="on"
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={closeAddTaskModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={!newTaskText.trim()}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Edit Task Modal */}
            {showEditTaskModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="editTaskModalTitle"
                        onMouseDown={(e) => {
                            // Click outside closes
                            if (e.target === e.currentTarget) closeEditTaskModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        await handleEditTaskSubmit();
                                    }}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="editTaskModalTitle">Edit Task</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={closeEditTaskModal}
                                        ></button>
                                    </div>

                                    <div className="modal-body">
                                        <div className="mb-3">
                                            <label htmlFor="editTaskInput" className="form-label">Task Name</label>
                                            <input
                                                id="editTaskInput"
                                                ref={editTaskInputRef}
                                                type="text"
                                                className="form-control"
                                                value={editTaskText}
                                                onChange={(e) => setEditTaskText(e.target.value)}
                                                placeholder="Task name…"
                                                autoCapitalize="sentences"
                                                autoCorrect="on"
                                                inputMode="text"
                                                enterKeyHint="done"
                                            />
                                        </div>
                                        <div className="mb-0">
                                            <label className="form-label">
                                                Due Date <span className="text-muted">(optional)</span>
                                            </label>
                                            
                                            {!editTaskDueDate ? (
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary w-100"
                                                        onClick={() => {
                                                            // TODO: Implement date/time picker
                                                            console.log('Set Date and Time clicked');
                                                        }}
                                                    >
                                                        <i className="bi bi-calendar-plus me-2"></i>
                                                        Set Date and Time
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="alert alert-info mb-2 d-flex justify-content-between align-items-center">
                                                        <span>
                                                            <i className="bi bi-calendar-check me-2"></i>
                                                            {editTaskDueDate.toLocaleString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            })}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex gap-2 mt-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary flex-grow-1"
                                                            onClick={() => {
                                                                // TODO: Implement date/time picker for editing
                                                                console.log('Change date clicked');
                                                            }}
                                                        >
                                                            <i className="bi bi-pencil me-1"></i>
                                                            Change
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger flex-grow-1"
                                                            onClick={() => {
                                                                setEditTaskDueDate(null);
                                                            }}
                                                        >
                                                            <i className="bi bi-x-circle me-1"></i>
                                                            Clear
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={closeEditTaskModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={!editTaskText.trim()}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Add Section Modal */}
            {showAddSectionModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="addSectionModalTitle"
                        onMouseDown={(e) => {
                            // Click outside closes
                            if (e.target === e.currentTarget) closeAddSectionModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        await handleAddSectionSubmit();
                                    }}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="addSectionModalTitle">Add Section</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={closeAddSectionModal}
                                        ></button>
                                    </div>

                                    <div className="modal-body">
                                        <input
                                            id="newSectionInput"
                                            ref={addSectionInputRef}
                                            type="text"
                                            className="form-control"
                                            value={newSectionTitle}
                                            onChange={(e) => setNewSectionTitle(e.target.value)}
                                            placeholder="Section name…"
                                            autoCapitalize="sentences"
                                            autoCorrect="on"
                                            inputMode="text"
                                            enterKeyHint="done"
                                        />
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={closeAddSectionModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={!newSectionTitle.trim()}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Rename Section Modal */}
            {showRenameSectionModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="renameSectionModalTitle"
                        onMouseDown={(e) => {
                            // Click outside closes
                            if (e.target === e.currentTarget) closeRenameSectionModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        await handleRenameSectionSubmit();
                                    }}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="renameSectionModalTitle">Rename Section</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={closeRenameSectionModal}
                                        ></button>
                                    </div>

                                    <div className="modal-body">
                                        <input
                                            id="renameSectionInput"
                                            ref={renameSectionInputRef}
                                            type="text"
                                            className="form-control"
                                            value={renameSectionTitle}
                                            onChange={(e) => setRenameSectionTitle(e.target.value)}
                                            placeholder="Section name…"
                                            autoCapitalize="sentences"
                                            autoCorrect="on"
                                            inputMode="text"
                                            enterKeyHint="done"
                                        />
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={closeRenameSectionModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={!renameSectionTitle.trim()}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Edit note modal */}
            {showEditNoteModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="editNoteModalTitle"
                        onMouseDown={(e) => {
                            // Click outside closes
                            if (e.target === e.currentTarget) closeEditNoteModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        await handleEditNoteSubmit();
                                    }}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="editNoteModalTitle">
                                            {editingNoteTask?.note && editingNoteTask.note.trim() ? 'Edit Note' : 'Add Note'}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={closeEditNoteModal}
                                        ></button>
                                    </div>

                                    <div className="modal-body">
                                        <div className="mb-2 small text-muted">
                                            <strong>Task:</strong> {editingNoteTask?.text}
                                        </div>
                                        <textarea
                                            id="editNoteInput"
                                            ref={editNoteInputRef}
                                            className="form-control"
                                            value={editNoteText}
                                            onChange={(e) => setEditNoteText(e.target.value)}
                                            placeholder="Add a note for this task…"
                                            rows="5"
                                            autoCapitalize="sentences"
                                            autoCorrect="on"
                                            style={{ resize: 'vertical' }}
                                        />
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={closeEditNoteModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Add User to List modal */}
            {showAddUserModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="addUserModalTitle"
                        onMouseDown={(e) => {
                            // Click outside closes
                            if (e.target === e.currentTarget) closeAddUserModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        await handleAddUserSubmit();
                                    }}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="addUserModalTitle">
                                            Add User to List
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={closeAddUserModal}
                                        ></button>
                                    </div>

                                    <div className="modal-body">
                                        <p className="text-muted mb-3">
                                            Enter the username of the user you want to grant access to this list.
                                        </p>
                                        <input
                                            ref={addUserUsernameRef}
                                            type="text"
                                            className="form-control"
                                            value={addUserUsername}
                                            onChange={(e) => setAddUserUsername(e.target.value)}
                                            placeholder="Enter username…"
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            inputMode="text"
                                            enterKeyHint="done"
                                        />
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={closeAddUserModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={!addUserUsername.trim()}
                                        >
                                            Add User
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Manage Users Modal */}
            {showManageUsersModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="manageUsersModalTitle"
                        onMouseDown={(e) => {
                            // Click outside closes
                            if (e.target === e.currentTarget) closeManageUsersModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="manageUsersModalTitle">
                                        Manage Users
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={closeManageUsersModal}
                                    ></button>
                                </div>

                                <div className="modal-body">
                                    {loadingUsers ? (
                                        <div className="text-center py-3">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : listUsers.length === 0 ? (
                                        <p className="text-muted mb-0">
                                            No other users have access to this list.
                                        </p>
                                    ) : (
                                        <div className="list-group">
                                            {listUsers.map((listUser) => (
                                                <div 
                                                    key={listUser.user_id} 
                                                    className="list-group-item d-flex justify-content-between align-items-center"
                                                >
                                                    <div>
                                                        <div className="fw-bold">{listUser.username}</div>
                                                        <small className="text-muted">
                                                            Added {new Date(listUser.created_at).toLocaleDateString()}
                                                            {listUser.granted_by_username && ` by ${listUser.granted_by_username}`}
                                                        </small>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => {
                                                            handleRemoveUser(
                                                                manageUsersListId,
                                                                listUser.user_id,
                                                                listUser.username
                                                            );
                                                        }}
                                                        title={`Remove ${listUser.username}`}
                                                    >
                                                        <i className="bi bi-person-x"></i>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeManageUsersModal}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Create List Modal */}
            {showCreateListModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="createListModalTitle"
                        onMouseDown={(e) => {
                            if (e.target === e.currentTarget) closeCreateListModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                                <form onSubmit={handleCreateList}>
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="createListModalTitle">
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Create New List
                                        </h5>
                                        <button type="button" className="btn-close" 
                                            aria-label="Close" onClick={closeCreateListModal}></button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="mb-1">
                                            <label htmlFor="modalListName" className="form-label fw-semibold">
                                                List Name *
                                            </label>
                                            <input
                                                ref={createListInputRef}
                                                type="text"
                                                className="form-control"
                                                id="modalListName"
                                                value={newListName}
                                                onChange={(e) => setNewListName(e.target.value)}
                                                placeholder="Enter list name..."
                                                required
                                                autoCapitalize="sentences"
                                                autoCorrect="on"
                                                inputMode="text"
                                                enterKeyHint="next"
                                            />
                                        </div>
                                        <div className="mb-1">
                                            <label htmlFor="modalListDescription" className="form-label fw-semibold">
                                                Description
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="modalListDescription"
                                                value={newListDescription}
                                                onChange={(e) => setNewListDescription(e.target.value)}
                                                placeholder="Brief description (optional)..."
                                                autoCapitalize="sentences"
                                                autoCorrect="on"
                                                inputMode="text"
                                                enterKeyHint="next"
                                            />
                                        </div>
                                        <div className="mb-1">
                                            <label htmlFor="modalActiveHeader" className="form-label fw-semibold">
                                                {newListType === 'simple' ? 'Section Title' : 'Active Section Title'}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="modalActiveHeader"
                                                value={editingActiveHeader}
                                                onChange={(e) => setEditingActiveHeader(e.target.value)}
                                                placeholder={newListType === 'simple' ? 'Items' : 'Active Tasks'}
                                            />
                                        </div>
                                        {newListType !== 'simple' && (
                                            <div className="mb-1">
                                                <label htmlFor="modalCompletedHeader" className="form-label fw-semibold">
                                                    Completed Section Title
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="modalCompletedHeader"
                                                    value={editingCompletedHeader}
                                                    onChange={(e) => setEditingCompletedHeader(e.target.value)}
                                                    placeholder="Completed Tasks"
                                                />
                                            </div>
                                        )}
                                        <div className="mb-1">
                                            <label htmlFor="modalListType" className="form-label fw-semibold">
                                                <i className="bi bi-list-ul me-2"></i>
                                                List Type *
                                            </label>
                                            <select
                                                className="form-select"
                                                id="modalListType"
                                                value={newListType}
                                                onChange={(e) => setNewListType(e.target.value)}
                                                required
                                            >
                                                <option value="checklist">Checklist - Items can be checked off</option>
                                                <option value="simple">Simple List - Just a list of items</option>
                                            </select>
                                            <div className="form-text">
                                                Choose whether items can be checked off or are just plain text
                                            </div>
                                        </div>
                                        <div className="mb-1">
                                            <label htmlFor="modalTaskOrdering" className="form-label fw-semibold">
                                                <i className="bi bi-sort-down me-2"></i>
                                                Default Task Ordering
                                            </label>
                                            <select
                                                className="form-select"
                                                id="modalTaskOrdering"
                                                value={newListTaskOrder}
                                                onChange={(e) => setNewListTaskOrder(e.target.value)}
                                            >
                                                <option value="custom">Custom (Drag & Drop)</option>
                                                <option value="alphabetical_asc">Alphabetical A-Z</option>
                                                <option value="alphabetical_desc">Alphabetical Z-A</option>
                                                <option value="date_asc">Date (Oldest First)</option>
                                                <option value="date_desc">Date (Newest First)</option>
                                            </select>
                                            <div className="form-text">
                                                Choose the default task ordering (users can customize their own view)
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-outline-secondary" 
                                            onClick={closeCreateListModal}>Cancel</button>
                                        <button type="submit" className="btn btn-success" 
                                            disabled={!newListName.trim()}>
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Create List
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Bulk Add Tasks Modal */}
            {showBulkAddModal && (
                <>
                    <div className="modal-backdrop fade show" aria-hidden="true"></div>
                    <div
                        className="modal fade show"
                        style={{ display: 'block' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="bulkAddModalTitle"
                        onMouseDown={(e) => {
                            if (e.target === e.currentTarget) closeBulkAddModal();
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <form onSubmit={handleBulkAddTask}>
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="bulkAddModalTitle">
                                            Bulk Add Tasks
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={closeBulkAddModal}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <p className="text-muted small mb-3">
                                            Add tasks one at a time. Press Enter or click "Add" to add each task. 
                                            Click "Done" when finished.
                                        </p>
                                        <div className="mb-3">
                                            <label htmlFor="bulkAddTaskInput" className="form-label">Task name</label>
                                            <input
                                                id="bulkAddTaskInput"
                                                ref={bulkAddInputRef}
                                                type="text"
                                                className="form-control"
                                                value={bulkAddTaskText}
                                                onChange={(e) => setBulkAddTaskText(e.target.value)}
                                                placeholder="Enter task text..."
                                                autoCapitalize="sentences"
                                                autoCorrect="on"
                                                inputMode="text"
                                                enterKeyHint="send"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="bulkAddTaskNote" className="form-label">
                                                Note <span className="text-muted small">(optional)</span>
                                            </label>
                                            <textarea
                                                id="bulkAddTaskNote"
                                                className="form-control"
                                                value={bulkAddTaskNote}
                                                onChange={(e) => setBulkAddTaskNote(e.target.value)}
                                                placeholder="Add a note…"
                                                rows="3"
                                                autoCapitalize="sentences"
                                                autoCorrect="on"
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={closeBulkAddModal}
                                        >
                                            Done
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={!bulkAddTaskText.trim()}
                                            onMouseDown={(e) => {
                                                // Prevent button from stealing focus, which would close keyboard on mobile
                                                e.preventDefault();
                                            }}
                                        >
                                            <i className="bi bi-plus-circle me-1"></i>
                                            Add
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
